// ARGOS Product Marketplace Types

export type ProductStatus = 'available' | 'coming_soon' | 'beta' | 'enterprise_only';
export type ProductCategory = 'plume_modeling' | 'compliance' | 'monitoring' | 'reporting' | 'analytics' | 'remediation' | 'document_management';
export type SubscriptionTier = 'starter' | 'professional' | 'enterprise';

// Filter dimension types
export type WorkflowStage = 'site_assessment' | 'risk_analysis' | 'remediation_planning' |
                            'performance_monitoring' | 'emergency_response' | 'decommissioning';

export type ProductDomain = 'groundwater_transport' | 'vapor_indoor_air' | 'remediation_optimization' |
                            'regulatory_compliance' | 'oil_gas_operations' | 'emerging_contaminants' |
                            'feedyard_environmental' | 'air_quality' | 'water_quality' | 'soil_management';

export type ProductIndustry = 'environmental_consulting' | 'oil_gas' | 'regulatory_affairs' | 'water_utilities' | 'agriculture' | 'cafo_operations';

export interface ProductPricing {
  tier: SubscriptionTier;
  monthlyPrice: number | null; // null = contact sales
  annualPrice: number | null;
  features: string[];
  siteLimit: number | null; // null = unlimited
  userLimit: number | null;
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  description: string;
  category: ProductCategory;
  status: ProductStatus;
  icon: string; // Icon name from heroicons
  accentColor: string; // Tailwind color class
  features: ProductFeature[];
  useCases: string[];
  pricing: ProductPricing[];
  technicalSpecs: TechnicalSpec[];
  heroImage?: string;
  demoUrl?: string;
  docsUrl?: string;
  // Filter dimensions
  workflowStages: WorkflowStage[];
  domains: ProductDomain[];
  industries: ProductIndustry[];
}

export interface ProductFeature {
  title: string;
  description: string;
  icon?: string;
}

export interface TechnicalSpec {
  label: string;
  value: string;
}

export interface ProductSubscription {
  id: string;
  clientId: string;
  productId: string;
  tier: SubscriptionTier;
  status: 'active' | 'trial' | 'suspended' | 'cancelled';
  startDate: string;
  endDate?: string;
  trialEndsAt?: string;
  siteLimit: number;
  userLimit: number;
}

// Product catalog
export const PRODUCT_CATEGORIES: Record<ProductCategory, { label: string; description: string }> = {
  plume_modeling: {
    label: 'Plume Modeling',
    description: 'Contamination plume prediction and analysis',
  },
  compliance: {
    label: 'Compliance',
    description: 'Regulatory tracking and compliance management',
  },
  monitoring: {
    label: 'Monitoring',
    description: 'Real-time environmental monitoring solutions',
  },
  reporting: {
    label: 'Reporting',
    description: 'Automated report generation and analytics',
  },
  analytics: {
    label: 'Analytics',
    description: 'Advanced data analytics and insights',
  },
  remediation: {
    label: 'Remediation',
    description: 'Strategy recommendation and treatment selection',
  },
  document_management: {
    label: 'Document Management',
    description: 'Centralized document storage and organization',
  },
};

// Workflow stage metadata (for organizing marketplace by project lifecycle)
export const WORKFLOW_STAGES: Record<WorkflowStage, { label: string; description: string; order: number }> = {
  site_assessment: {
    label: 'Site Assessment',
    description: 'Initial site characterization and contamination evaluation',
    order: 1,
  },
  risk_analysis: {
    label: 'Risk Analysis',
    description: 'Risk evaluation, prioritization, and regulatory pathway analysis',
    order: 2,
  },
  remediation_planning: {
    label: 'Remediation Planning',
    description: 'Strategy selection and treatment design',
    order: 3,
  },
  performance_monitoring: {
    label: 'Performance Monitoring',
    description: 'System optimization and progress tracking',
    order: 4,
  },
  emergency_response: {
    label: 'Emergency Response',
    description: 'Rapid response tools for spills and incidents',
    order: 5,
  },
  decommissioning: {
    label: 'Decommissioning',
    description: 'Asset retirement and closure planning',
    order: 6,
  },
};

// Domain/contaminant type metadata
export const PRODUCT_DOMAINS: Record<ProductDomain, { label: string; description: string }> = {
  groundwater_transport: {
    label: 'Groundwater Fate & Transport',
    description: 'Plume modeling, migration prediction, and natural attenuation',
  },
  vapor_indoor_air: {
    label: 'Vapor & Indoor Air',
    description: 'Vapor intrusion pathway assessment',
  },
  remediation_optimization: {
    label: 'Remediation Optimization',
    description: 'System performance and treatment efficiency',
  },
  regulatory_compliance: {
    label: 'Regulatory & Compliance',
    description: 'Regulatory pathway analysis and compliance tracking',
  },
  oil_gas_operations: {
    label: 'Oil & Gas Operations',
    description: 'Oilfield-specific environmental tools',
  },
  emerging_contaminants: {
    label: 'Emerging Contaminants',
    description: 'PFAS and other emerging contaminant analysis',
  },
  feedyard_environmental: {
    label: 'Feedyard Environmental',
    description: 'CAFO and feedlot environmental management',
  },
  air_quality: {
    label: 'Air Quality',
    description: 'Emissions, odor, and particulate matter analysis',
  },
  water_quality: {
    label: 'Water Quality',
    description: 'Surface and groundwater quality monitoring',
  },
  soil_management: {
    label: 'Soil Management',
    description: 'Soil health, contamination, and nutrient management',
  },
};

// Industry/sector metadata
export const PRODUCT_INDUSTRIES: Record<ProductIndustry, { label: string; description: string }> = {
  environmental_consulting: {
    label: 'Environmental Consulting',
    description: 'Tools for environmental consultants and engineers',
  },
  oil_gas: {
    label: 'Oil & Gas',
    description: 'Upstream and midstream environmental management',
  },
  regulatory_affairs: {
    label: 'Regulatory Affairs',
    description: 'Compliance and regulatory professionals',
  },
  water_utilities: {
    label: 'Water Utilities',
    description: 'Municipal and private water systems',
  },
  agriculture: {
    label: 'Agriculture',
    description: 'Farming and ranching operations',
  },
  cafo_operations: {
    label: 'CAFO Operations',
    description: 'Concentrated Animal Feeding Operations management',
  },
};

// Polygon product definition
export const POLYGON_PRODUCT: Product = {
  id: 'polygon',
  slug: 'polygon',
  name: 'POLYGON',
  tagline: 'Groundwater Contaminant Modeling and Prediction Engine',
  description: 'Ensemble forecaster for BTEX and TPH plume behavior — from current state through 20 years out. Three-plume simulation (PSH + dissolved benzene + dissolved TPH) with automatic delineation-well recommendations and a remediation-impact calculator that predicts cost savings before you commit capital.',
  category: 'plume_modeling',
  status: 'available',
  icon: 'PolygonIcon',
  accentColor: 'cyan',
  heroImage: '/polygon-hero.jpg',
  features: [
    {
      title: '2D Plume Map',
      description: 'Three-plume simulation (PSH + dissolved benzene + dissolved TPH) on satellite tiles. Smooth contour rendering with future-horizon plume polygons drawn against the active regulatory MCL. Ctrl-scroll zoom.',
      icon: 'MapIcon',
    },
    {
      title: '3D Plume Scene',
      description: 'Volumetric 3D rendering of all three plumes with vertical exaggeration. Well markers, plume sources, and contour bands sit on the same scene as 2D — toggle between views without losing time-slider state.',
      icon: 'CubeTransparentIcon',
    },
    {
      title: 'Multi-Well Multi-Analyte Chart',
      description: 'Professional-grade hydrograph showing every well, every analyte, and every prediction horizon as connected points on one chart — pan, zoom, crosshair tooltip, per-series visibility.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Well Predictions',
      description: 'Per-well per-analyte forecasts at multiple horizons from current through 20 years out, in a sortable table. Each cell flags exceedance against the active regulatory library.',
      icon: 'TableCellsIcon',
    },
    {
      title: 'Delineation Intelligence',
      description: 'Priority-ranked monitoring well recommendations driven by future-horizon exceedance, distance from source, and trigger-well combined rank. Inline DW rename, plan-and-place, live cost integration.',
      icon: 'MapPinIcon',
    },
    {
      title: 'Cost Analysis',
      description: 'Multi-horizon liability curve — Start Now, 20-Year Liability, Delineation costs. Forecasts are reliability-capped per analyte so the cost engine only commits where the model is confident. Cost escalation timeline, sentinel-well count, and active remediation system tracking.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'Remediation Impact Calculator',
      description: 'Capture-zone simulation of remediation systems against the modeled plume. Predicts PSH/dissolved mass reduction and 20-year liability savings for MDPE, pump-and-treat, ISCO, and bioremediation combinations.',
      icon: 'CalculatorIcon',
    },
    {
      title: 'Ensemble Forecasting Engine',
      description: 'A stacked machine-learning ensemble that combines multiple model families at every prediction horizon. Per-analyte bundles (BTEX, TPH) with built-in reliability flags so the cost engine knows when to cap forecasts.',
      icon: 'CpuChipIcon',
    },
  ],
  useCases: [
    'Petroleum UST release sites and bulk terminal BTEX/TPH plumes',
    'Industrial facility legacy contamination and chlorinated solvent plumes',
    'RCRA corrective action compliance monitoring with multi-horizon forecasting',
    'Multi-state portfolio capital allocation against 20-year liability curves',
    'Regulatory negotiation backed by per-aquifer, per-analyte forecasts at named horizons',
    'Remediation system planning — model PSH/dissolved mass reduction before approving capex',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited sites',
        'Ensemble forecaster across multiple horizons through 20 years',
        'Three-plume simulation (PSH + dissolved benzene + dissolved TPH)',
        'Multi-horizon cost engine with per-analyte reliability capping',
        'Delineation Intelligence with priority ranking',
        'Remediation Impact Calculator',
        'Smooth contour rendering',
        'State regulatory library cascade',
        'Portfolio prioritization with 20-year liability ranking',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
        'Custom model training',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'ML Model', value: 'Stacked machine-learning ensemble with meta-blended outputs' },
    { label: 'Prediction Horizons', value: 'Current through 20 years, with multiple intermediate marks' },
    { label: 'Analytes', value: 'BTEX (benzene primary) + TPH bundles' },
    { label: 'Plume Modeling', value: 'Three-plume — PSH, dissolved benzene, dissolved TPH' },
    { label: 'Contour Engine', value: 'Physics-based interpolation with client-side contour rendering' },
    { label: 'Cost Engine', value: 'Multi-horizon liability curve with per-analyte reliability capping' },
    { label: 'Remediation Model', value: 'Capture-zone simulation with decay and pump-rate constraints' },
  ],
  docsUrl: '/docs/polygon',
  workflowStages: ['site_assessment'],
  domains: ['groundwater_transport'],
  industries: ['environmental_consulting'],
};

// Remediation System Performance Optimizer product definition
export const PULSE_PRODUCT: Product = {
  id: 'pulse',
  slug: 'pulse',
  name: 'PULSE',
  tagline: 'Remediation System Performance Optimizer',
  description: 'Remediation system performance analysis using proprietary Performance Score algorithms. Detect asymptotic behavior, optimize technology transitions, and predict closure timelines with cost projections.',
  category: 'analytics',
  status: 'available',
  icon: 'PulseWaveIcon',
  accentColor: 'cyan',
  heroImage: '/pulse-hero.jpg',
  features: [
    {
      title: 'Performance Score (0-100)',
      description: 'Proprietary metric combining mass removal rate, efficiency trend, cost per pound removed, and time to asymptote into a single actionable score.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Asymptotic Analysis',
      description: 'Detects when remediation systems approach diminishing returns using time-series analysis and inflection point detection algorithms.',
      icon: 'ArrowTrendingDownIcon',
    },
    {
      title: 'Technology Transition Recommendations',
      description: 'Model-driven suggestions for when to switch from active remediation to MNA or alternative technologies based on performance trends.',
      icon: 'ArrowPathIcon',
    },
    {
      title: 'Closure Timeline Prediction',
      description: 'Monte Carlo simulation-based closure date predictions with confidence intervals and regulatory milestone tracking.',
      icon: 'CalendarDaysIcon',
    },
    {
      title: 'Cost to Closure Analysis',
      description: 'Real-time cost projections combining remaining mass, current efficiency, and technology transition scenarios.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'System Benchmarking',
      description: 'Compare your remediation system performance against similar sites in our database of 500+ historical projects.',
      icon: 'ScaleIcon',
    },
  ],
  useCases: [
    'Pump and treat system optimization for groundwater remediation',
    'SVE/AS system performance monitoring and transition planning',
    'ISCO/ISCR injection program effectiveness tracking',
    'Multi-phase extraction system efficiency analysis',
    'Regulatory closure negotiations with data-driven projections',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited sites',
        'Performance Score dashboard',
        'Asymptotic detection alerts',
        'Technology transition recommendations',
        'Closure timeline predictions',
        'Cost to closure analysis',
        'System benchmarking',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'ML Model', value: 'XGBoost + ARIMA Hybrid' },
    { label: 'Performance Metrics', value: '12 weighted factors' },
    { label: 'Prediction Horizon', value: '1-60 months' },
    { label: 'Confidence Intervals', value: 'Monte Carlo (1000 simulations)' },
    { label: 'Training Data', value: '500+ remediation systems' },
    { label: 'Update Frequency', value: 'Weekly performance refresh' },
  ],
  docsUrl: '/docs/pulse',
  workflowStages: ['performance_monitoring'],
  domains: ['remediation_optimization'],
  industries: ['environmental_consulting'],
};

// PATHFINDER product definition
export const PATHFINDER_PRODUCT: Product = {
  id: 'pathfinder',
  slug: 'pathfinder',
  name: 'PATHFINDER',
  tagline: 'Remediation Strategy Recommendation Engine',
  description: 'Remediation technology selection and cost estimation with integrated service provider matching. Input your site characteristics and get ranked recommendations for optimal treatment strategies, then instantly connect with qualified environmental firms in your area whose capabilities match the recommended technologies.',
  category: 'remediation',
  status: 'available',
  icon: 'PathfinderIcon',
  accentColor: 'cyan',
  heroImage: '/pathfinder-hero.jpg',
  features: [
    {
      title: 'Technology Scoring Engine',
      description: 'Evaluates 10+ remediation technologies against your site characteristics using rule-based logic and ML-based outcome prediction.',
      icon: 'SparklesIcon',
    },
    {
      title: 'Service Provider Matching',
      description: 'Cross-references recommended technologies with qualified environmental firms in your area. Scores providers by capability match, proximity, ratings, and project experience.',
      icon: 'UserGroupIcon',
    },
    {
      title: 'Cost Estimation',
      description: 'Capital cost, annual O&M, and total lifecycle cost estimates based on plume size, soil type, treatment technology, and regional cost factors.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'Timeline Projections',
      description: 'Installation, active remediation, and monitoring phase durations with optimistic, expected, and pessimistic scenarios.',
      icon: 'ClockIcon',
    },
    {
      title: 'Success Probability',
      description: 'Historical success rates for similar sites using the same technology, with confidence intervals and risk factors.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Side-by-Side Comparison',
      description: 'Compare multiple technologies across effectiveness, cost, timeline, and sustainability metrics with matched providers for each.',
      icon: 'ScaleIcon',
    },
  ],
  useCases: [
    'New site assessment with immediate contractor recommendations',
    'RFP development with pre-qualified vendor shortlists',
    'Evaluating alternatives for underperforming existing systems',
    'Budget planning with regional cost adjustments and vendor quotes',
    'Multi-site portfolio strategy with local provider networks',
    'Technology transition planning with specialized contractor matching',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'Full technology comparison',
        'Service provider matching & recommendations',
        'Cost and timeline estimates',
        'Success probability analysis',
        'Regional cost adjustments',
        'Provider capability verification',
        'Historical outcome data',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Technologies Evaluated', value: '10+ remediation methods' },
    { label: 'Provider Database', value: 'National network of qualified firms' },
    { label: 'Matching Algorithm', value: '40% capability, 30% proximity, 20% rating, 10% experience' },
    { label: 'Cost Model', value: 'Per-sqft + regional factors' },
    { label: 'Historical Database', value: '500+ outcome records' },
    { label: 'Output Format', value: 'Ranked technologies + matched providers' },
  ],
  docsUrl: '/docs/pathfinder',
  workflowStages: ['remediation_planning'],
  domains: ['remediation_optimization'],
  industries: ['environmental_consulting'],
};

// PERMEATE product definition
export const PERMEATE_PRODUCT: Product = {
  id: 'permeate',
  slug: 'permeate',
  name: 'PERMEATE',
  tagline: 'Vapor Intrusion Risk Predictor',
  description: 'Physics-informed vapor intrusion pathway modeling using the Johnson & Ettinger (1991) framework enhanced with machine learning. Predict indoor air concentrations from subsurface contamination sources with site-specific attenuation factors.',
  category: 'analytics',
  status: 'available',
  icon: 'CloudArrowUpIcon',
  accentColor: 'cyan',
  heroImage: '/permeate-hero.jpg',
  features: [
    {
      title: 'Johnson-Ettinger Physics Engine',
      description: 'Industry-standard EPA model for vapor transport through unsaturated zone with capillary effects and diffusion calculations.',
      icon: 'BeakerIcon',
    },
    {
      title: 'Attenuation Factor Calculation',
      description: 'Calculates groundwater-to-soil-gas and soil-gas-to-indoor attenuation factors based on site-specific geology and building parameters.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Indoor Air Concentration Prediction',
      description: 'Predicts indoor air concentrations for VOCs like TCE, PCE, and benzene with uncertainty quantification.',
      icon: 'HomeModernIcon',
    },
    {
      title: 'Screening Level Comparison',
      description: 'Automatic comparison to EPA RSLs and state-specific screening levels with exceedance flagging.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Mitigation Recommendations',
      description: 'Provides mitigation strategy recommendations based on predicted concentrations and risk levels.',
      icon: 'WrenchScrewdriverIcon',
    },
    {
      title: 'Building Parameter Sensitivity',
      description: 'Analyze how foundation type, air exchange rate, and building size affect vapor intrusion risk.',
      icon: 'AdjustmentsHorizontalIcon',
    },
  ],
  useCases: [
    'Brownfield redevelopment vapor intrusion assessments',
    'TCE/PCE dry cleaner site evaluations',
    'UST release vapor intrusion screening',
    'Indoor air quality investigations',
    'Vapor barrier design and verification',
    'Real estate due diligence assessments',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'Johnson-Ettinger calculations',
        'Attenuation factor modeling',
        'Indoor air predictions',
        'Screening level comparison',
        'Mitigation recommendations',
        'Sensitivity analysis',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Physics Model', value: 'Johnson & Ettinger (1991)' },
    { label: 'ML Enhancement', value: 'XGBoost correction factors' },
    { label: 'Contaminants', value: 'TCE, PCE, Benzene, VOCs' },
    { label: 'Attenuation Factors', value: 'GW-to-soil, Soil-to-indoor' },
    { label: 'Screening Databases', value: 'EPA RSL, State-specific' },
    { label: 'Output Format', value: 'Risk assessment report' },
  ],
  docsUrl: '/docs/permeate',
  workflowStages: ['site_assessment'],
  domains: ['vapor_indoor_air'],
  industries: ['environmental_consulting'],
};

// PLUNGE product definition
export const PLUNGE_PRODUCT: Product = {
  id: 'plunge',
  slug: 'plunge',
  name: 'PLUNGE',
  tagline: 'DNAPL Location Predictor',
  description: 'Invasion percolation-based DNAPL migration modeling to predict likely locations of dense non-aqueous phase liquid accumulations. Combines geology, release history, and dissolved plume data to identify high-probability DNAPL source zones.',
  category: 'plume_modeling',
  status: 'available',
  icon: 'ArrowDownCircleIcon',
  accentColor: 'cyan',
  heroImage: '/plunge-hero.jpg',
  features: [
    {
      title: 'Invasion Percolation Physics',
      description: 'Models DNAPL migration through heterogeneous geology using invasion percolation theory for accurate pooling predictions.',
      icon: 'CubeIcon',
    },
    {
      title: 'Bedrock Depression Finder',
      description: 'Analyzes bedrock topography to identify low points where DNAPL accumulates as pools.',
      icon: 'MapIcon',
    },
    {
      title: 'Vertical Migration Analysis',
      description: 'Calculates DNAPL penetration depths and horizontal spreading based on release volume and soil properties.',
      icon: 'ArrowsPointingInIcon',
    },
    {
      title: 'Dissolved Plume Backtracking',
      description: 'Uses dissolved-phase plume geometry to back-calculate likely DNAPL source locations.',
      icon: 'ArrowUturnLeftIcon',
    },
    {
      title: 'Probability Heatmaps',
      description: 'Generates spatial probability maps showing likelihood of DNAPL presence at each location.',
      icon: 'FireIcon',
    },
    {
      title: 'Investigation Recommendations',
      description: 'Prioritized locations for direct-push sampling and soil borings to confirm DNAPL presence.',
      icon: 'MagnifyingGlassIcon',
    },
  ],
  useCases: [
    'Chlorinated solvent site source zone delineation',
    'Creosote and coal tar site investigations',
    'PCB oil release source identification',
    'Fractured bedrock DNAPL investigations',
    'Investigation work plan optimization',
    'Remedial design source zone targeting',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'Invasion percolation modeling',
        'Bedrock depression analysis',
        'Vertical migration calculations',
        'Probability heatmaps',
        'Investigation recommendations',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Physics Model', value: 'Invasion Percolation Theory' },
    { label: 'ML Enhancement', value: 'XGBoost probability calibration' },
    { label: 'DNAPL Types', value: 'TCE, PCE, Creosote, Coal Tar' },
    { label: 'Geology Layers', value: 'Multi-layer stratigraphy' },
    { label: 'Output Format', value: 'Probability map + recommendations' },
    { label: 'Spatial Resolution', value: 'User-defined grid' },
  ],
  docsUrl: '/docs/plunge',
  workflowStages: ['site_assessment'],
  domains: ['groundwater_transport'],
  industries: ['environmental_consulting'],
};

// PEAK product definition
export const PEAK_PRODUCT: Product = {
  id: 'peak',
  slug: 'peak',
  name: 'PEAK',
  tagline: 'Asymptotic Recovery Predictor',
  description: 'Predict when remediation systems will hit diminishing returns using curve fitting analysis. Exponential decay and power law models identify the optimal transition point from active remediation to monitored natural attenuation.',
  category: 'analytics',
  status: 'available',
  icon: 'ArrowTrendingDownIcon',
  accentColor: 'cyan',
  heroImage: '/peak-hero.jpg',
  features: [
    {
      title: 'Curve Fitting Engine',
      description: 'Fits exponential decay and power law models to recovery data to predict asymptotic behavior.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Asymptote Detection',
      description: 'Identifies the month when recovery rate drops below economically viable thresholds.',
      icon: 'FlagIcon',
    },
    {
      title: 'Transition Point Analysis',
      description: 'Calculates optimal timing for transitioning from active remediation to MNA or closure.',
      icon: 'ArrowPathIcon',
    },
    {
      title: 'Recovery Projections',
      description: '12 and 24-month recovery projections with confidence intervals and model fit statistics.',
      icon: 'CalendarDaysIcon',
    },
    {
      title: 'Cost-Benefit Analysis',
      description: 'Compares cost per pound recovered now vs. at asymptote to quantify diminishing returns.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'System Transition Recommendations',
      description: 'Specific recommendations for technology changes or closure based on recovery trends.',
      icon: 'LightBulbIcon',
    },
  ],
  useCases: [
    'Pump and treat system optimization',
    'SVE system shutdown decisions',
    'Multi-phase extraction efficiency analysis',
    'Regulatory closure negotiations',
    'Technology transition planning',
    'O&M budget optimization',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited system analyses',
        'Curve fitting analysis',
        'Asymptote detection',
        'Transition recommendations',
        'Recovery projections',
        'Cost-benefit analysis',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Curve Models', value: 'Exponential Decay, Power Law' },
    { label: 'ML Enhancement', value: 'XGBoost model selection' },
    { label: 'System Types', value: 'P&T, SVE, MPE, ISCO, Bioremediation' },
    { label: 'Projection Horizon', value: '1-60 months' },
    { label: 'Confidence Intervals', value: 'Bootstrap estimation' },
    { label: 'Output Format', value: 'Timeline + recommendations' },
  ],
  docsUrl: '/docs/peak',
  workflowStages: ['performance_monitoring'],
  domains: ['remediation_optimization'],
  industries: ['environmental_consulting'],
};

// PHASE product definition
export const PHASE_PRODUCT: Product = {
  id: 'phase',
  slug: 'phase',
  name: 'PHASE',
  tagline: 'LNAPL Recoverability Analyzer',
  description: 'Multiphase flow modeling for light non-aqueous phase liquid (LNAPL) recovery assessment. Uses Van Genuchten capillary model to convert apparent thickness to actual volume and predict recoverable fractions.',
  category: 'analytics',
  status: 'available',
  icon: 'BeakerIcon',
  accentColor: 'cyan',
  heroImage: '/phase-hero.jpg',
  features: [
    {
      title: 'Van Genuchten Capillary Model',
      description: 'Industry-standard multiphase flow model for converting well measurements to actual LNAPL distribution.',
      icon: 'BeakerIcon',
    },
    {
      title: 'Exaggeration Factor Calculation',
      description: 'Calculates the ratio of apparent thickness in well to actual LNAPL thickness in formation.',
      icon: 'ArrowsPointingOutIcon',
    },
    {
      title: 'Volume Estimation',
      description: 'Estimates total, recoverable, and residual LNAPL volumes based on soil properties and saturation.',
      icon: 'CubeIcon',
    },
    {
      title: 'Transmissivity Analysis',
      description: 'Calculates LNAPL transmissivity to predict recovery rates and mobility classification.',
      icon: 'ArrowRightCircleIcon',
    },
    {
      title: 'Recovery Technology Selection',
      description: 'Recommends optimal recovery technologies based on transmissivity and volume estimates.',
      icon: 'WrenchScrewdriverIcon',
    },
    {
      title: 'Smear Zone Analysis',
      description: 'Identifies smear zone conditions from water table fluctuation and recommends appropriate strategies.',
      icon: 'ArrowsUpDownIcon',
    },
  ],
  useCases: [
    'UST release LNAPL recovery feasibility',
    'Petroleum terminal free product recovery',
    'LNAPL baildown test interpretation',
    'Recovery system design optimization',
    'Regulatory LNAPL body delineation',
    'Closure strategy development',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'Van Genuchten modeling',
        'Volume estimation',
        'Transmissivity analysis',
        'Technology recommendations',
        'Smear zone analysis',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Physics Model', value: 'Van Genuchten Capillary' },
    { label: 'ML Enhancement', value: 'XGBoost recovery prediction' },
    { label: 'Product Types', value: 'Gasoline, Diesel, Jet Fuel, Crude' },
    { label: 'Soil Types', value: 'Sand, Silt, Clay, Loam' },
    { label: 'Mobility Classes', value: 'Mobile, Limited, Residual' },
    { label: 'Output Format', value: 'Volume report + recommendations' },
  ],
  docsUrl: '/docs/phase',
  workflowStages: ['remediation_planning'],
  domains: ['remediation_optimization'],
  industries: ['environmental_consulting'],
};

// PROXY product definition
export const PROXY_PRODUCT: Product = {
  id: 'proxy',
  slug: 'proxy',
  name: 'PROXY',
  tagline: 'Spatial Concentration Predictor',
  description: 'Geostatistical interpolation using Ordinary Kriging to predict contaminant concentrations at unsampled locations. Generates isoconcentration contours with uncertainty quantification and optimal sampling recommendations.',
  category: 'plume_modeling',
  status: 'available',
  icon: 'MapIcon',
  accentColor: 'cyan',
  heroImage: '/proxy-hero.jpg',
  features: [
    {
      title: 'Ordinary Kriging Engine',
      description: 'Industry-standard geostatistical interpolation that honors sample data and quantifies prediction uncertainty.',
      icon: 'MapIcon',
    },
    {
      title: 'Variogram Fitting',
      description: 'Automatic fitting of spherical, exponential, or Gaussian variogram models to characterize spatial correlation.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Concentration Predictions',
      description: 'Predicts concentrations at any location with standard error and 95% confidence intervals.',
      icon: 'CalculatorIcon',
    },
    {
      title: 'Isoconcentration Contours',
      description: 'Generates contour maps for plume visualization and regulatory boundary delineation.',
      icon: 'GlobeAmericasIcon',
    },
    {
      title: 'Uncertainty Mapping',
      description: 'Maps prediction uncertainty to identify areas needing additional characterization.',
      icon: 'QuestionMarkCircleIcon',
    },
    {
      title: 'Optimal Sampling Locations',
      description: 'Recommends new sample locations that maximize variance reduction per sample.',
      icon: 'MapPinIcon',
    },
  ],
  useCases: [
    'Groundwater plume delineation',
    'Soil contamination mapping',
    'Data gap analysis and fill-in sampling',
    'Regulatory boundary determination',
    'Remedial design concentration estimates',
    'Long-term monitoring optimization',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited analyses',
        'Kriging interpolation',
        'Variogram fitting',
        'Uncertainty mapping',
        'Optimal sampling recommendations',
        'Grid generation tools',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Interpolation Method', value: 'Ordinary Kriging' },
    { label: 'Variogram Models', value: 'Spherical, Exponential, Gaussian' },
    { label: 'ML Enhancement', value: 'XGBoost residual correction' },
    { label: 'Minimum Samples', value: '4+ for variogram fitting' },
    { label: 'Output Format', value: 'Predictions + uncertainty + contours' },
    { label: 'Grid Resolution', value: 'User-defined spacing' },
  ],
  docsUrl: '/docs/proxy',
  workflowStages: ['site_assessment'],
  domains: ['groundwater_transport'],
  industries: ['environmental_consulting'],
};

// PERSIST product definition
export const PERSIST_PRODUCT: Product = {
  id: 'persist',
  slug: 'persist',
  name: 'PERSIST',
  tagline: 'Natural Attenuation Rate Calculator',
  description: 'Calculate first-order decay rates and assess monitored natural attenuation (MNA) feasibility. Analyzes concentration trends, geochemical footprint, and estimates time to reach cleanup standards.',
  category: 'analytics',
  status: 'available',
  icon: 'ClockIcon',
  accentColor: 'cyan',
  heroImage: '/persist-hero.jpg',
  features: [
    {
      title: 'First-Order Decay Analysis',
      description: 'Fits exponential decay model to concentration time series to calculate site-specific decay rates.',
      icon: 'ArrowTrendingDownIcon',
    },
    {
      title: 'Half-Life Calculation',
      description: 'Converts decay rates to half-lives for intuitive understanding of attenuation timescales.',
      icon: 'ClockIcon',
    },
    {
      title: 'Geochemical Evidence Assessment',
      description: 'Evaluates electron acceptors, metabolic byproducts, and redox conditions for biodegradation evidence.',
      icon: 'BeakerIcon',
    },
    {
      title: 'MNA Feasibility Scoring',
      description: 'Combines trend analysis and geochemical evidence into actionable feasibility assessment.',
      icon: 'CheckBadgeIcon',
    },
    {
      title: 'Time to Cleanup Projection',
      description: 'Estimates years to reach cleanup standards based on current trends and target concentrations.',
      icon: 'CalendarDaysIcon',
    },
    {
      title: 'Regulatory Documentation',
      description: 'Generates technical justification for MNA remedy selection following EPA guidance.',
      icon: 'DocumentTextIcon',
    },
  ],
  useCases: [
    'BTEX plume MNA feasibility assessment',
    'Chlorinated solvent natural attenuation evaluation',
    'Long-term monitoring trend analysis',
    'Remedy transition to MNA justification',
    'Regulatory closure demonstrations',
    'Cost-benefit analysis of active vs. passive remediation',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'Decay rate calculation',
        'Geochemical assessment',
        'MNA feasibility scoring',
        'Time to cleanup projections',
        'Regulatory documentation',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Kinetics Model', value: 'First-Order Decay' },
    { label: 'ML Enhancement', value: 'XGBoost rate prediction' },
    { label: 'Geochemical Parameters', value: 'DO, NO3, SO4, Fe(II), CH4' },
    { label: 'Minimum Samples', value: '4+ per well for trend' },
    { label: 'Guidance Framework', value: 'EPA MNA protocols' },
    { label: 'Output Format', value: 'Feasibility report + timeline' },
  ],
  docsUrl: '/docs/persist',
  workflowStages: ['performance_monitoring'],
  domains: ['groundwater_transport'],
  industries: ['environmental_consulting'],
};

// PIPELINE product definition
export const PIPELINE_PRODUCT: Product = {
  id: 'pipeline',
  slug: 'pipeline',
  name: 'PIPELINE',
  tagline: 'Infrastructure Failure Risk Predictor',
  description: 'Predict pipeline and underground infrastructure failure probability using corrosion modeling and Weibull survival analysis. Prioritize inspection and replacement investments based on quantified risk.',
  category: 'analytics',
  status: 'available',
  icon: 'WrenchScrewdriverIcon',
  accentColor: 'cyan',
  heroImage: '/pipeline-hero.jpg',
  features: [
    {
      title: 'Corrosion Rate Modeling',
      description: 'Calculates material-specific corrosion rates based on soil resistivity, coating, and cathodic protection status.',
      icon: 'BeakerIcon',
    },
    {
      title: 'Weibull Survival Analysis',
      description: 'Statistical failure prediction using industry-standard survival analysis methodology.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Stress Analysis',
      description: 'Barlow formula calculations for hoop stress and remaining wall thickness safety factors.',
      icon: 'CpuChipIcon',
    },
    {
      title: '10-Year Failure Probability',
      description: 'Quantified failure probability over planning horizon for risk-based prioritization.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Remaining Life Estimation',
      description: 'Estimates years until failure threshold based on corrosion progression models.',
      icon: 'ClockIcon',
    },
    {
      title: 'Prioritized Action Plans',
      description: 'Ranked inspection and replacement recommendations with cost estimates.',
      icon: 'ClipboardDocumentListIcon',
    },
  ],
  useCases: [
    'Underground storage tank piping assessment',
    'Petroleum pipeline integrity management',
    'Municipal water/sewer infrastructure planning',
    'Industrial process piping risk assessment',
    'Capital planning and budget allocation',
    'Regulatory compliance documentation',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited segment analyses',
        'Corrosion modeling',
        'Survival analysis',
        'Stress calculations',
        'Failure probability',
        'Prioritized action plans',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Corrosion Model', value: 'Material-specific rates' },
    { label: 'Survival Analysis', value: 'Weibull distribution' },
    { label: 'Stress Calculation', value: 'Barlow formula' },
    { label: 'ML Enhancement', value: 'XGBoost risk calibration' },
    { label: 'Materials', value: 'Steel, Cast Iron, Ductile Iron, PVC' },
    { label: 'Output Format', value: 'Risk assessment + action plan' },
  ],
  docsUrl: '/docs/pipeline',
  workflowStages: ['risk_analysis'],
  domains: ['oil_gas_operations'],
  industries: ['oil_gas'],
};

// PROTOCOL product definition
export const PROTOCOL_PRODUCT: Product = {
  id: 'protocol',
  slug: 'protocol',
  name: 'PROTOCOL',
  tagline: 'TRRP Closure Pathway Optimizer',
  description: 'Analysis of Texas Risk Reduction Program closure pathways. Compare Plan A (numerical standards), Plan B (site-specific risk assessment), and Plan C (institutional controls) to find the fastest, most cost-effective route to regulatory closure with TCEQ acceptance prediction.',
  category: 'compliance',
  status: 'available',
  icon: 'DocumentCheckIcon',
  accentColor: 'cyan',
  heroImage: '/protocol-hero.jpg',
  features: [
    {
      title: 'TRRP Pathway Analysis Engine',
      description: 'Evaluates all three TRRP closure pathways against your site data to identify the optimal route to closure.',
      icon: 'MapIcon',
    },
    {
      title: 'Cost Comparison Calculator',
      description: 'Side-by-side cost estimates for Plan A, B, and C including remediation, monitoring, and institutional controls.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'Timeline Estimation',
      description: 'Realistic timeline projections for each pathway based on regulatory review times and required activities.',
      icon: 'ClockIcon',
    },
    {
      title: 'Data Gap Identification',
      description: 'Automatically identifies missing data needed to support each closure pathway.',
      icon: 'ExclamationCircleIcon',
    },
    {
      title: 'TCEQ Acceptance Predictor',
      description: 'ML-based prediction of TCEQ acceptance probability for each pathway based on site characteristics.',
      icon: 'CheckBadgeIcon',
    },
    {
      title: 'Regulatory Documentation Generator',
      description: 'Generates supporting documentation and closure strategy recommendations for TCEQ submission.',
      icon: 'DocumentTextIcon',
    },
  ],
  useCases: [
    'LPST site closure strategy selection',
    'VCP site pathway optimization',
    'Industrial site TRRP compliance',
    'Cost-benefit analysis for closure alternatives',
    'Regulatory negotiation support',
    'Multi-site portfolio closure planning',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'All TRRP pathway comparisons',
        'Cost and timeline estimates',
        'TCEQ acceptance predictions',
        'Data gap analysis',
        'Documentation generation',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Pathways Analyzed', value: 'Plan A, B, C' },
    { label: 'PCL Database', value: 'TRRP Tier 1 (100+ compounds)' },
    { label: 'ML Model', value: 'XGBoost acceptance predictor' },
    { label: 'Cost Model', value: 'Activity-based costing' },
    { label: 'Regulatory Framework', value: 'TCEQ TRRP Rules' },
    { label: 'Output Format', value: 'Pathway comparison report' },
  ],
  docsUrl: '/docs/protocol',
  workflowStages: ['risk_analysis'],
  domains: ['regulatory_compliance'],
  industries: ['regulatory_affairs'],
};

// PINNACLE product definition
export const PINNACLE_PRODUCT: Product = {
  id: 'pinnacle',
  slug: 'pinnacle',
  name: 'PINNACLE',
  tagline: 'Site Prioritization Engine',
  description: 'Portfolio prioritization predicting TCEQ and RRC regulatory priority scores. Rank your entire portfolio by enforcement risk, optimize capital allocation, and prevent regulatory actions before they happen.',
  category: 'analytics',
  status: 'available',
  icon: 'ChartBarSquareIcon',
  accentColor: 'cyan',
  heroImage: '/pinnacle-hero.jpg',
  features: [
    {
      title: 'Multi-Agency Priority Prediction',
      description: 'Predicts priority scores for TCEQ PST, TCEQ IHW, RRC, and EPA programs using agency-specific algorithms.',
      icon: 'BuildingLibraryIcon',
    },
    {
      title: 'Enforcement Risk Scoring',
      description: 'Quantifies enforcement risk on 0-100 scale with predicted days until likely agency action.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Portfolio Ranking Algorithm',
      description: 'Ranks all sites by combined priority, enforcement risk, and capital efficiency scores.',
      icon: 'ArrowTrendingUpIcon',
    },
    {
      title: 'Capital Allocation Optimizer',
      description: 'Recommends budget allocation percentages across risk tiers to maximize portfolio risk reduction.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'Regulatory Action Predictor',
      description: 'Predicts specific regulatory actions (NOVs, orders, penalties) and their likelihood.',
      icon: 'ShieldExclamationIcon',
    },
    {
      title: 'Compliance Timeline Generator',
      description: 'Creates prioritized action timelines with deadlines to maintain compliance.',
      icon: 'CalendarDaysIcon',
    },
  ],
  useCases: [
    'Multi-site portfolio prioritization',
    'Annual capital planning and budgeting',
    'Enforcement risk management',
    'Regulatory compliance tracking',
    'Executive portfolio reporting',
    'M&A environmental liability assessment',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited portfolio sites',
        'Multi-agency priority prediction',
        'Enforcement risk scoring',
        'Portfolio ranking',
        'Capital allocation recommendations',
        'Compliance timelines',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Agencies Supported', value: 'TCEQ, RRC, EPA' },
    { label: 'ML Model', value: 'XGBoost ensemble' },
    { label: 'Risk Factors', value: '25+ weighted factors' },
    { label: 'Priority Systems', value: 'TCEQ 1-4, RRC, EPA' },
    { label: 'Update Frequency', value: 'Real-time on data change' },
    { label: 'Output Format', value: 'Ranked portfolio + actions' },
  ],
  docsUrl: '/docs/pinnacle',
  workflowStages: ['risk_analysis'],
  domains: ['regulatory_compliance'],
  industries: ['regulatory_affairs'],
};

// PLUG product definition
export const PLUG_PRODUCT: Product = {
  id: 'plug',
  slug: 'plug',
  name: 'PLUG',
  tagline: 'Well Plugging Cost Estimator',
  description: 'ML-powered well plugging cost estimation trained on 10,000+ actual plugging operations. Predict costs by depth, condition, location, and required formation isolations per RRC Statewide Rule 13.',
  category: 'analytics',
  status: 'available',
  icon: 'WrenchScrewdriverIcon',
  accentColor: 'cyan',
  heroImage: '/plug-hero.jpg',
  features: [
    {
      title: 'Cost Prediction Engine',
      description: 'ML model trained on 10,000+ historical plugging operations predicts costs with ±20% accuracy.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'Formation Isolation Requirements',
      description: 'Automatically identifies required cement plugs based on formations penetrated and Rule 13.',
      icon: 'Square3Stack3DIcon',
    },
    {
      title: 'RRC District Cost Comparison',
      description: 'Compares your estimate to RRC per-foot costs by district for financial assurance planning.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Financial Assurance Calculator',
      description: 'Calculates recommended bond amounts and compares to RRC minimum requirements.',
      icon: 'BanknotesIcon',
    },
    {
      title: 'Plug vs Extend Decision Support',
      description: 'NPV analysis comparing plug-now vs. extend-and-wait strategies with recommendation.',
      icon: 'ScaleIcon',
    },
    {
      title: 'Batch Portfolio Analysis',
      description: 'Analyze entire well portfolios with prioritized plugging order and budget allocation.',
      icon: 'QueueListIcon',
    },
  ],
  useCases: [
    'P&A budget planning and forecasting',
    'Financial assurance requirement calculation',
    'Plugging contractor bid evaluation',
    'ARO liability estimation',
    'Well portfolio acquisition due diligence',
    'Orphan well program cost estimation',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited well analyses',
        'ML cost prediction',
        'RRC district comparisons',
        'Financial assurance calculator',
        'Plug vs extend analysis',
        'Batch portfolio analysis',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Training Data', value: '10,000+ plugging operations' },
    { label: 'ML Model', value: 'XGBoost regressor' },
    { label: 'Cost Factors', value: 'Depth, condition, district, H2S' },
    { label: 'RRC Districts', value: 'All 10 Texas districts' },
    { label: 'Accuracy', value: '±20% on validation set' },
    { label: 'Output Format', value: 'Cost breakdown + timeline' },
  ],
  docsUrl: '/docs/plug',
  workflowStages: ['decommissioning'],
  domains: ['oil_gas_operations'],
  industries: ['oil_gas'],
};

// POUNCE product definition
export const POUNCE_PRODUCT: Product = {
  id: 'pounce',
  slug: 'pounce',
  name: 'POUNCE',
  tagline: 'Emergency Spill Response Estimator',
  description: 'Instant cost estimation for spill response from initial call information. Predict total response costs, resource requirements, and timelines within minutes of incident notification.',
  category: 'analytics',
  status: 'available',
  icon: 'BoltIcon',
  accentColor: 'cyan',
  heroImage: '/pounce-hero.jpg',
  features: [
    {
      title: 'Rapid Cost Estimation Engine',
      description: 'Generate cost estimates within minutes using limited initial incident information.',
      icon: 'ClockIcon',
    },
    {
      title: 'Resource Requirements Calculator',
      description: 'Calculates personnel, equipment, and specialized resources needed for response.',
      icon: 'UserGroupIcon',
    },
    {
      title: 'Response Phase Breakdown',
      description: 'Detailed cost and timeline estimates for emergency, recovery, delineation, remediation, and closure phases.',
      icon: 'ListBulletIcon',
    },
    {
      title: 'Spill Spread Predictor',
      description: 'Predicts spill spread based on volume, surface type, weather, and time since release.',
      icon: 'ArrowsPointingOutIcon',
    },
    {
      title: 'Regulatory Notification Tracker',
      description: 'Identifies required notifications to NRC, RRC, TCEQ, and local agencies with deadlines.',
      icon: 'BellAlertIcon',
    },
    {
      title: 'Immediate Action Checklist',
      description: 'Generates prioritized action items for the first hours of response.',
      icon: 'CheckCircleIcon',
    },
  ],
  useCases: [
    'Emergency spill response cost estimation',
    'Insurance claim preparation',
    'Response resource mobilization planning',
    'Regulatory notification compliance',
    'Incident command support',
    'Post-incident cost reconciliation',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited incident analyses',
        'Rapid cost estimation',
        'Resource planning',
        'Phase breakdown',
        'Spread prediction',
        'Regulatory tracking',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Spill Types', value: 'Crude, diesel, gasoline, chemicals' },
    { label: 'ML Model', value: 'XGBoost cost predictor' },
    { label: 'Surface Types', value: 'Soil, water, concrete, wetland' },
    { label: 'Estimation Speed', value: '< 2 minutes' },
    { label: 'Regulatory Coverage', value: 'NRC, RRC, TCEQ, local' },
    { label: 'Output Format', value: 'Response plan + cost estimate' },
  ],
  docsUrl: '/docs/pounce',
  workflowStages: ['emergency_response'],
  domains: ['oil_gas_operations'],
  industries: ['oil_gas'],
};

// PARALLAX product definition
export const PARALLAX_PRODUCT: Product = {
  id: 'parallax',
  slug: 'parallax',
  name: 'PARALLAX',
  tagline: 'Corridor Environmental Assessment Tool',
  description: 'Automated environmental screening for linear infrastructure projects (TxDOT, pipelines, transmission lines). Search multiple databases simultaneously and identify recognized environmental conditions along project corridors.',
  category: 'compliance',
  status: 'available',
  icon: 'MapIcon',
  accentColor: 'cyan',
  heroImage: '/parallax-hero.jpg',
  features: [
    {
      title: 'Multi-Database Search Engine',
      description: 'Simultaneously searches EPA, TCEQ, RRC, and historical databases within corridor buffer.',
      icon: 'MagnifyingGlassIcon',
    },
    {
      title: 'Historical Land Use Analysis',
      description: 'Reviews Sanborn maps, historical aerials, and city directories for historical RECs.',
      icon: 'ClockIcon',
    },
    {
      title: 'REC Identification Algorithm',
      description: 'Automatically classifies findings as REC, CREC, HREC, or de minimis conditions.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Corridor Risk Mapping',
      description: 'Visual mapping of environmental risk along corridor with station-based references.',
      icon: 'MapPinIcon',
    },
    {
      title: 'Phase II Recommendations',
      description: 'Prioritized list of locations requiring Phase II ESA investigation.',
      icon: 'BeakerIcon',
    },
    {
      title: 'Investigation Cost Estimator',
      description: 'Estimates total Phase II investigation costs for identified RECs.',
      icon: 'CurrencyDollarIcon',
    },
  ],
  useCases: [
    'TxDOT highway project environmental screening',
    'Pipeline route environmental assessment',
    'Transmission line corridor screening',
    'Railroad corridor environmental review',
    'Linear infrastructure Phase I ESAs',
    'Right-of-way acquisition due diligence',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited corridor analyses',
        'Multi-database search',
        'Historical records review',
        'REC classification',
        'Risk mapping',
        'Phase II recommendations',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Databases Searched', value: 'EPA, TCEQ, RRC, historical' },
    { label: 'Buffer Distance', value: 'User-defined (default 0.5 mi)' },
    { label: 'REC Classification', value: 'ASTM E1527-21 aligned' },
    { label: 'Historical Sources', value: 'Aerials, Sanborn, directories' },
    { label: 'Corridor Types', value: 'Highway, pipeline, transmission' },
    { label: 'Output Format', value: 'GIS-ready + report' },
  ],
  docsUrl: '/docs/parallax',
  workflowStages: ['decommissioning'],
  domains: ['regulatory_compliance'],
  industries: ['regulatory_affairs'],
};

// PRECIPITATE product definition
export const PRECIPITATE_PRODUCT: Product = {
  id: 'precipitate',
  slug: 'precipitate',
  name: 'PRECIPITATE',
  tagline: 'Produced Water Plume Predictor',
  description: 'Specialized modeling for brine and produced water contamination. Predict chloride plume migration, soil damage extent, and remediation timelines for oilfield saltwater releases.',
  category: 'plume_modeling',
  status: 'available',
  icon: 'BeakerIcon',
  accentColor: 'cyan',
  heroImage: '/precipitate-hero.jpg',
  features: [
    {
      title: 'Conservative Transport Modeling',
      description: 'Physics-based chloride transport modeling through vadose zone and saturated aquifer.',
      icon: 'ArrowPathIcon',
    },
    {
      title: 'Soil Damage Assessment',
      description: 'Quantifies soil damage extent, severity zones, and sodium adsorption ratio impacts.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Agricultural Impact Prediction',
      description: 'Predicts vegetation kill zones, crop loss estimates, and recovery timelines.',
      icon: 'SunIcon',
    },
    {
      title: 'Plume Migration Forecast',
      description: '6, 12, and 24-month plume extent predictions with migration rate calculations.',
      icon: 'ArrowTrendingUpIcon',
    },
    {
      title: 'Remediation Cost Estimator',
      description: 'Estimates soil remediation, groundwater treatment, and monitoring costs.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'RRC Reporting Support',
      description: 'Generates supporting data for Form H-8 and remediation plan submissions.',
      icon: 'DocumentTextIcon',
    },
  ],
  useCases: [
    'Oilfield saltwater spill assessment',
    'Produced water pipeline leak evaluation',
    'Tank battery release analysis',
    'Agricultural damage claim support',
    'RRC remediation planning',
    'Saltwater disposal well failure assessment',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited release analyses',
        'Chloride transport modeling',
        'Soil damage assessment',
        'Agricultural impact prediction',
        'Plume forecasting',
        'Remediation cost estimates',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Transport Model', value: 'Conservative tracer (chloride)' },
    { label: 'ML Enhancement', value: 'XGBoost spread prediction' },
    { label: 'Soil Types', value: 'USDA texture classes' },
    { label: 'Damage Thresholds', value: 'TCEQ/RRC standards' },
    { label: 'Forecast Horizon', value: '1-24 months' },
    { label: 'Output Format', value: 'Damage assessment + forecast' },
  ],
  docsUrl: '/docs/precipitate',
  workflowStages: ['remediation_planning'],
  domains: ['groundwater_transport'],
  industries: ['oil_gas'],
};

// PERPETUAL product definition
export const PERPETUAL_PRODUCT: Product = {
  id: 'perpetual',
  slug: 'perpetual',
  name: 'PERPETUAL',
  tagline: 'PFAS Risk Predictor',
  description: 'PFAS (forever chemicals) contamination assessment and treatment recommendation engine. Predicts PFAS transport, evaluates MCL compliance under EPA 2024 Final Rule, and recommends optimal treatment technologies based on compound-specific properties.',
  category: 'analytics',
  status: 'available',
  icon: 'BeakerIcon',
  accentColor: 'cyan',
  heroImage: '/perpetual-hero.jpg',
  features: [
    {
      title: 'EPA 2024 MCL Compliance',
      description: 'Evaluates against new EPA Maximum Contaminant Levels: PFOA/PFOS at 4.0 ng/L, PFHxS/PFNA/HFPO-DA at 10 ng/L.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'Multi-Compound Analysis',
      description: 'Analyzes 12+ PFAS compounds including PFOA, PFOS, PFNA, PFHxS, PFHpA, PFDA, GenX (HFPO-DA), and precursors.',
      icon: 'BeakerIcon',
    },
    {
      title: 'Transport Modeling',
      description: 'Koc-based retardation factor calculations for PFAS transport through soil and groundwater.',
      icon: 'ArrowPathIcon',
    },
    {
      title: 'Treatment Technology Scoring',
      description: 'Ranks GAC, ion exchange, reverse osmosis, and other technologies by effectiveness for your PFAS mixture.',
      icon: 'SparklesIcon',
    },
    {
      title: 'Drinking Water Risk Assessment',
      description: 'Evaluates well risk based on distance, gradient, and predicted plume migration timing.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Source Identification',
      description: 'Identifies likely PFAS sources based on compound fingerprinting and land use analysis.',
      icon: 'MagnifyingGlassIcon',
    },
  ],
  useCases: [
    'AFFF (firefighting foam) release assessment',
    'Industrial facility PFAS contamination',
    'Landfill PFAS leachate evaluation',
    'Municipal water supply protection',
    'DoD/military base PFAS assessment',
    'PFAS treatment system design',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'EPA MCL compliance checks',
        'Multi-compound assessment',
        'Transport modeling',
        'Treatment recommendations',
        'Risk assessment',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'PFAS Compounds', value: '12+ including PFOA, PFOS, GenX' },
    { label: 'Regulatory Framework', value: 'EPA 2024 Final PFAS Rule' },
    { label: 'Transport Model', value: 'Koc-based retardation' },
    { label: 'Treatment Technologies', value: 'GAC, IX, RO, AOPs' },
    { label: 'ML Enhancement', value: 'XGBoost fate prediction' },
    { label: 'Output Format', value: 'Risk assessment + treatment plan' },
  ],
  docsUrl: '/docs/perpetual',
  workflowStages: ['site_assessment'],
  domains: ['emerging_contaminants'],
  industries: ['environmental_consulting', 'water_utilities'],
};

// PROTON product definition
export const PROTON_PRODUCT: Product = {
  id: 'proton',
  slug: 'proton',
  name: 'PROTON',
  tagline: 'NORM Predictor',
  description: 'Naturally Occurring Radioactive Material (NORM) prediction for oil and gas equipment decommissioning. Predicts Ra-226 activity levels, determines disposal classifications, and estimates decommissioning costs per RRC and TCEQ requirements.',
  category: 'analytics',
  status: 'available',
  icon: 'BoltIcon',
  accentColor: 'cyan',
  heroImage: '/proton-hero.jpg',
  features: [
    {
      title: 'NORM Activity Prediction',
      description: 'Predicts Ra-226 activity levels in pCi/g based on formation, equipment type, and production history.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Disposal Classification',
      description: 'Automatically classifies NORM waste as exempt (<5 pCi/g), low, elevated, or high activity.',
      icon: 'TagIcon',
    },
    {
      title: 'Equipment Risk Assessment',
      description: 'Scores NORM risk for separators, heater-treaters, tanks, tubing, and other oilfield equipment.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Decommissioning Cost Estimator',
      description: 'Estimates total decommissioning costs including survey, handling, transport, and disposal.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'Survey Recommendations',
      description: 'Recommends appropriate survey methods (screening, characterization, or full radiological).',
      icon: 'MagnifyingGlassIcon',
    },
    {
      title: 'Regulatory Compliance',
      description: 'Ensures compliance with RRC Statewide Rule 91, TCEQ 30 TAC 336, and API RP 1110.',
      icon: 'DocumentCheckIcon',
    },
  ],
  useCases: [
    'Oilfield equipment decommissioning planning',
    'NORM survey prioritization',
    'Disposal cost budgeting',
    'Scale/sludge management',
    'Well plugging NORM assessment',
    'Facility abandonment planning',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited equipment analyses',
        'NORM activity prediction',
        'Disposal classification',
        'Cost estimation',
        'Survey recommendations',
        'Regulatory compliance',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Isotopes', value: 'Ra-226, Ra-228, Pb-210' },
    { label: 'Equipment Types', value: 'Separator, tank, tubing, etc.' },
    { label: 'Disposal Classes', value: 'Exempt, Low, Elevated, High' },
    { label: 'Regulatory Framework', value: 'RRC Rule 91, TCEQ 336' },
    { label: 'ML Enhancement', value: 'XGBoost activity predictor' },
    { label: 'Output Format', value: 'Assessment + disposal plan' },
  ],
  docsUrl: '/docs/proton',
  workflowStages: ['site_assessment'],
  domains: ['oil_gas_operations'],
  industries: ['oil_gas'],
};

// PYRITIC product definition
export const PYRITIC_PRODUCT: Product = {
  id: 'pyritic',
  slug: 'pyritic',
  name: 'PYRITIC',
  tagline: 'H2S Risk Predictor',
  description: 'Hydrogen Sulfide (H2S) concentration prediction and safety planning for drilling and remediation operations. Predicts H2S levels based on formation, determines contingency requirements per RRC Statewide Rule 36, and generates site-specific safety plans.',
  category: 'analytics',
  status: 'available',
  icon: 'ExclamationTriangleIcon',
  accentColor: 'cyan',
  heroImage: '/pyritic-hero.jpg',
  features: [
    {
      title: 'Formation-Based H2S Prediction',
      description: 'Predicts H2S concentrations using formation characteristics, offset well data, and depth adjustments.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Radius of Exposure Calculator',
      description: 'Calculates RRC-compliant radius of exposure and public protection zones per Rule 36.',
      icon: 'MapPinIcon',
    },
    {
      title: 'Contingency Level Determination',
      description: 'Determines required contingency level (1-4) based on predicted H2S and receptor proximity.',
      icon: 'ShieldExclamationIcon',
    },
    {
      title: 'PPE Requirements',
      description: 'Specifies required personal protective equipment and safety systems for predicted H2S levels.',
      icon: 'UserIcon',
    },
    {
      title: 'Public Notification Requirements',
      description: 'Identifies when public notification is required and generates notification procedures.',
      icon: 'MegaphoneIcon',
    },
    {
      title: 'Safety Plan Generator',
      description: 'Creates site-specific H2S contingency plans with evacuation routes and emergency procedures.',
      icon: 'DocumentTextIcon',
    },
  ],
  useCases: [
    'Drilling operations H2S planning',
    'Workover and completion safety',
    'Well plugging H2S assessment',
    'Production operations safety',
    'Remediation site safety planning',
    'RRC Rule 36 compliance',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site assessments',
        'H2S concentration prediction',
        'Radius of exposure calculation',
        'Contingency level determination',
        'PPE recommendations',
        'Safety plan generation',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Formations', value: 'Anadarko, Delaware, Wolfcamp, etc.' },
    { label: 'H2S Thresholds', value: '100 ppm (H2S area), 500 ppm (IDLH)' },
    { label: 'Regulatory Framework', value: 'RRC Statewide Rule 36' },
    { label: 'Contingency Levels', value: 'Level 1-4' },
    { label: 'ML Enhancement', value: 'XGBoost concentration predictor' },
    { label: 'Output Format', value: 'Assessment + contingency plan' },
  ],
  docsUrl: '/docs/pyritic',
  workflowStages: ['site_assessment'],
  domains: ['oil_gas_operations'],
  industries: ['oil_gas'],
};

// PASTURE product definition
export const PASTURE_PRODUCT: Product = {
  id: 'pasture',
  slug: 'pasture',
  name: 'PASTURE',
  tagline: 'Nutrient Application Optimizer',
  description: 'Nutrient management for feedyard waste application. Calculate optimal application rates based on soil tests, crop needs, and regulatory limits per TCEQ CAFO permit requirements.',
  category: 'analytics',
  status: 'available',
  icon: 'BeakerIcon',
  accentColor: 'cyan',
  heroImage: '/pasture-hero.jpg',
  features: [
    {
      title: 'Agronomic Rate Calculator',
      description: 'Calculates nitrogen-based and phosphorus-based agronomic rates using soil test data and crop nutrient uptake rates.',
      icon: 'CalculatorIcon',
    },
    {
      title: 'Soil Test Integration',
      description: 'Integrates soil test results for N, P, K, and micronutrients to optimize application recommendations.',
      icon: 'BeakerIcon',
    },
    {
      title: 'CAFO Permit Compliance',
      description: 'Ensures application rates comply with TCEQ CAFO permit NMP requirements and setback rules.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'Field Capacity Tracking',
      description: 'Tracks cumulative nutrient loading by field to prevent over-application and maintain compliance.',
      icon: 'MapIcon',
    },
    {
      title: 'Seasonal Recommendations',
      description: 'Provides season-specific application timing based on crop growth stage and weather patterns.',
      icon: 'CalendarDaysIcon',
    },
    {
      title: 'Cost Optimization',
      description: 'Calculates value of manure nutrients vs. commercial fertilizer to maximize economic benefit.',
      icon: 'CurrencyDollarIcon',
    },
  ],
  useCases: [
    'CAFO NMP development and compliance',
    'Land application rate optimization',
    'Nutrient budget balancing',
    'Setback and buffer verification',
    'Annual nutrient management reporting',
    'Field-by-field application planning',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited field analyses',
        'Agronomic rate calculations',
        'CAFO compliance tracking',
        'Seasonal recommendations',
        'Cost optimization',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'ML Model', value: 'XGBoost + Agronomic Engine' },
    { label: 'Crop Database', value: '50+ crops with uptake rates' },
    { label: 'Regulatory Framework', value: 'TCEQ CAFO Permits' },
    { label: 'Soil Types', value: 'USDA texture classes' },
    { label: 'Nutrients Tracked', value: 'N, P, K, micronutrients' },
    { label: 'Output Format', value: 'Application plan + compliance report' },
  ],
  docsUrl: '/docs/pasture',
  workflowStages: ['performance_monitoring'],
  domains: ['feedyard_environmental', 'soil_management'],
  industries: ['agriculture', 'cafo_operations'],
};

// POND product definition
export const POND_PRODUCT: Product = {
  id: 'pond',
  slug: 'pond',
  name: 'POND',
  tagline: 'Lagoon Integrity & Capacity Predictor',
  description: 'Lagoon management for feedyard waste storage. Predict freeboard levels, detect structural integrity issues, and optimize pumping schedules to maintain TCEQ compliance.',
  category: 'monitoring',
  status: 'available',
  icon: 'ArrowTrendingDownIcon',
  accentColor: 'cyan',
  heroImage: '/pond-hero.jpg',
  features: [
    {
      title: 'Freeboard Prediction',
      description: 'Predicts lagoon levels based on inflow, precipitation, evaporation, and pumping schedules.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Structural Integrity Assessment',
      description: 'Evaluates liner condition, berm stability, and seepage risk based on age and inspection data.',
      icon: 'ShieldCheckIcon',
    },
    {
      title: 'Capacity Forecasting',
      description: 'Projects available capacity over 30, 60, and 90 days with weather-adjusted scenarios.',
      icon: 'CalendarDaysIcon',
    },
    {
      title: 'Pumping Schedule Optimizer',
      description: 'Recommends optimal pumping times to maintain freeboard while minimizing energy costs.',
      icon: 'ArrowPathIcon',
    },
    {
      title: 'TCEQ Compliance Alerts',
      description: 'Alerts when freeboard approaches minimum requirements or when inspections are due.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Sludge Accumulation Tracking',
      description: 'Tracks sludge buildup and predicts cleanout timing to maintain design capacity.',
      icon: 'CubeIcon',
    },
  ],
  useCases: [
    'CAFO lagoon freeboard management',
    'Waste storage capacity planning',
    'Structural integrity monitoring',
    'Pumping schedule optimization',
    'TCEQ inspection preparation',
    'Multi-lagoon system management',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited lagoon analyses',
        'Freeboard prediction',
        'Integrity assessment',
        'Capacity forecasting',
        'Pumping optimization',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'ML Model', value: 'XGBoost + Water Balance' },
    { label: 'Prediction Horizon', value: '1-90 days' },
    { label: 'Regulatory Framework', value: 'TCEQ CAFO Permits' },
    { label: 'Weather Integration', value: 'NOAA forecast data' },
    { label: 'Integrity Factors', value: 'Liner, berms, seepage' },
    { label: 'Output Format', value: 'Level forecast + alerts' },
  ],
  docsUrl: '/docs/pond',
  workflowStages: ['performance_monitoring'],
  domains: ['feedyard_environmental', 'water_quality'],
  industries: ['agriculture', 'cafo_operations'],
};

// PERMIT product definition
export const PERMIT_PRODUCT: Product = {
  id: 'permit',
  slug: 'permit',
  name: 'PERMIT',
  tagline: 'CAFO Compliance Tracker',
  description: 'Comprehensive TCEQ CAFO permit compliance management. Track all permit requirements, deadlines, and documentation with automated alerts and audit-ready reporting.',
  category: 'compliance',
  status: 'available',
  icon: 'ShieldCheckIcon',
  accentColor: 'cyan',
  heroImage: '/permit-hero.jpg',
  features: [
    {
      title: 'Permit Requirement Tracking',
      description: 'Tracks all TCEQ CAFO General Permit requirements with status indicators and due dates.',
      icon: 'ClipboardDocumentListIcon',
    },
    {
      title: 'Compliance Score Dashboard',
      description: 'Real-time compliance score (0-100) based on weighted permit requirements and deadline adherence.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Automated Deadline Alerts',
      description: 'Proactive alerts for upcoming inspections, reports, certifications, and permit renewals.',
      icon: 'BellAlertIcon',
    },
    {
      title: 'Document Management',
      description: 'Centralized storage for NMPs, inspection records, water quality reports, and certifications.',
      icon: 'FolderIcon',
    },
    {
      title: 'Inspection Preparation',
      description: 'Generates pre-inspection checklists and identifies potential compliance gaps.',
      icon: 'MagnifyingGlassIcon',
    },
    {
      title: 'Audit Trail',
      description: 'Complete audit trail of compliance activities for regulatory review.',
      icon: 'DocumentTextIcon',
    },
  ],
  useCases: [
    'TCEQ CAFO permit compliance management',
    'Permit renewal preparation',
    'Inspection readiness',
    'Multi-facility compliance tracking',
    'Regulatory audit support',
    'Compliance documentation',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited facility tracking',
        'Compliance scoring',
        'Deadline management',
        'Document storage',
        'Audit reports',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Permit Types', value: 'TCEQ CAFO General & Individual' },
    { label: 'Requirements Tracked', value: '25+ permit conditions' },
    { label: 'Alert Lead Time', value: '30/60/90 day warnings' },
    { label: 'Compliance Factors', value: '15 weighted categories' },
    { label: 'Document Types', value: 'NMP, inspections, reports' },
    { label: 'Output Format', value: 'Dashboard + reports' },
  ],
  docsUrl: '/docs/permit',
  workflowStages: ['performance_monitoring'],
  domains: ['regulatory_compliance', 'feedyard_environmental'],
  industries: ['agriculture', 'cafo_operations', 'regulatory_affairs'],
};

// PERCOLATE product definition
export const PERCOLATE_PRODUCT: Product = {
  id: 'percolate',
  slug: 'percolate',
  name: 'PERCOLATE',
  tagline: 'Nitrate Leaching Predictor',
  description: 'Physics-informed nitrate leaching model for feedyard and land application sites. Predict groundwater nitrate concentrations using soil properties, application rates, and hydrogeology.',
  category: 'plume_modeling',
  status: 'available',
  icon: 'ArrowDownCircleIcon',
  accentColor: 'cyan',
  heroImage: '/percolate-hero.jpg',
  features: [
    {
      title: 'Nitrate Transport Modeling',
      description: 'Models nitrogen transformation and nitrate transport through the vadose zone to groundwater.',
      icon: 'ArrowPathIcon',
    },
    {
      title: 'Leaching Factor Calculation',
      description: 'Calculates site-specific leaching factors based on soil texture, climate, and irrigation.',
      icon: 'CalculatorIcon',
    },
    {
      title: 'Groundwater Impact Prediction',
      description: 'Predicts nitrate concentrations at the water table and time to impact.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'MCL Exceedance Risk',
      description: 'Assesses risk of exceeding the 10 mg/L nitrate MCL at receptors.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Application Rate Limits',
      description: 'Calculates maximum application rates to protect groundwater quality.',
      icon: 'ArrowTrendingDownIcon',
    },
    {
      title: 'Monitoring Well Recommendations',
      description: 'Recommends monitoring well locations and sampling frequencies.',
      icon: 'MapPinIcon',
    },
  ],
  useCases: [
    'Land application groundwater protection',
    'CAFO groundwater monitoring design',
    'Nitrate plume assessment',
    'Application rate optimization',
    'Regulatory compliance demonstration',
    'Long-term impact prediction',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'Leaching modeling',
        'Groundwater prediction',
        'MCL risk assessment',
        'Rate limit calculations',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Physics Model', value: 'Advection-Dispersion + Decay' },
    { label: 'ML Enhancement', value: 'XGBoost leaching calibration' },
    { label: 'N Transformations', value: 'Mineralization, nitrification' },
    { label: 'Soil Database', value: 'USDA SSURGO integration' },
    { label: 'Regulatory Standard', value: 'EPA MCL 10 mg/L NO3-N' },
    { label: 'Output Format', value: 'Concentration profile + risk' },
  ],
  docsUrl: '/docs/percolate',
  workflowStages: ['site_assessment', 'risk_analysis'],
  domains: ['groundwater_transport', 'feedyard_environmental'],
  industries: ['agriculture', 'cafo_operations', 'environmental_consulting'],
};

// PEN product definition
export const PEN_PRODUCT: Product = {
  id: 'pen',
  slug: 'pen',
  name: 'PEN',
  tagline: 'Feedyard Closure Cost Estimator',
  description: 'ML-powered closure cost estimation for feedyard operations. Predict total decommissioning costs including waste removal, soil remediation, structure demolition, and regulatory closure requirements.',
  category: 'analytics',
  status: 'available',
  icon: 'WrenchScrewdriverIcon',
  accentColor: 'cyan',
  heroImage: '/pen-hero.jpg',
  features: [
    {
      title: 'Closure Cost Prediction',
      description: 'ML model predicts total closure costs based on facility size, age, and contamination history.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'Component Cost Breakdown',
      description: 'Detailed breakdown by waste removal, soil remediation, structure demo, and monitoring.',
      icon: 'ListBulletIcon',
    },
    {
      title: 'Liability Assessment',
      description: 'Quantifies environmental liability for financial reporting and due diligence.',
      icon: 'ScaleIcon',
    },
    {
      title: 'Timeline Estimation',
      description: 'Projects closure timeline including regulatory approval and remediation phases.',
      icon: 'CalendarDaysIcon',
    },
    {
      title: 'Phased Closure Planning',
      description: 'Analyzes phased vs. complete closure scenarios with cost comparisons.',
      icon: 'ArrowPathIcon',
    },
    {
      title: 'Regulatory Requirements',
      description: 'Identifies all TCEQ closure requirements and generates compliance checklist.',
      icon: 'DocumentCheckIcon',
    },
  ],
  useCases: [
    'Feedyard decommissioning planning',
    'ARO liability estimation',
    'M&A environmental due diligence',
    'Financial reserve planning',
    'Closure strategy optimization',
    'Regulatory closure compliance',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited facility analyses',
        'Cost prediction',
        'Liability assessment',
        'Timeline estimation',
        'Scenario analysis',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'ML Model', value: 'XGBoost cost regressor' },
    { label: 'Cost Components', value: '8 closure categories' },
    { label: 'Training Data', value: '200+ historical closures' },
    { label: 'Accuracy', value: '±25% on validation set' },
    { label: 'Regulatory Framework', value: 'TCEQ closure requirements' },
    { label: 'Output Format', value: 'Cost breakdown + timeline' },
  ],
  docsUrl: '/docs/pen',
  workflowStages: ['decommissioning'],
  domains: ['feedyard_environmental'],
  industries: ['agriculture', 'cafo_operations'],
};

// PATHOGEN product definition (feedyard)
export const PATHOGEN_FEEDYARD_PRODUCT: Product = {
  id: 'pathogen',
  slug: 'pathogen',
  name: 'PATHOGEN',
  tagline: 'Microbial Contamination Predictor',
  description: 'Fate and transport modeling for pathogens from feedyard waste. Predict E. coli and fecal coliform concentrations at receptors using Chick-Watson decay kinetics and transport physics.',
  category: 'analytics',
  status: 'available',
  icon: 'DocumentChartBarIcon',
  accentColor: 'cyan',
  heroImage: '/pathogen-hero.jpg',
  features: [
    {
      title: 'Pathogen Decay Modeling',
      description: 'Models pathogen die-off using Chick-Watson first-order kinetics with temperature correction.',
      icon: 'ArrowTrendingDownIcon',
    },
    {
      title: 'Transport Pathway Analysis',
      description: 'Analyzes surface runoff, subsurface flow, and spray irrigation pathways.',
      icon: 'ArrowPathIcon',
    },
    {
      title: 'Receptor Concentration Prediction',
      description: 'Predicts E. coli concentrations at water bodies, wells, and other receptors.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Log Reduction Calculation',
      description: 'Calculates cumulative log reduction from all decay and treatment mechanisms.',
      icon: 'CalculatorIcon',
    },
    {
      title: 'Water Quality Standard Comparison',
      description: 'Compares predictions to TCEQ contact and non-contact recreation standards.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'Risk Level Classification',
      description: 'Classifies pathways as low, moderate, high, or critical risk.',
      icon: 'ExclamationTriangleIcon',
    },
  ],
  useCases: [
    'CAFO pathogen risk assessment',
    'Land application setback verification',
    'Surface water protection',
    'Well protection area analysis',
    'Regulatory compliance demonstration',
    'Treatment system design',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited pathway analyses',
        'Decay modeling',
        'Transport simulation',
        'Risk classification',
        'Standard comparison',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Decay Model', value: 'Chick-Watson first-order' },
    { label: 'ML Enhancement', value: 'XGBoost rate calibration' },
    { label: 'Pathogens', value: 'E. coli, fecal coliform' },
    { label: 'Pathways', value: 'Runoff, subsurface, spray' },
    { label: 'Standards', value: 'TCEQ 126/630 CFU/100mL' },
    { label: 'Output Format', value: 'Risk assessment + recommendations' },
  ],
  docsUrl: '/docs/pathogen',
  workflowStages: ['risk_analysis'],
  domains: ['feedyard_environmental', 'water_quality'],
  industries: ['agriculture', 'cafo_operations', 'environmental_consulting'],
};

// PLUVIUS product definition
export const PLUVIUS_PRODUCT: Product = {
  id: 'pluvius',
  slug: 'pluvius',
  name: 'PLUVIUS',
  tagline: 'Odor Dispersion Modeler',
  description: 'Gaussian plume dispersion modeling for feedyard odor emissions. Predict odor concentrations at receptors and assess nuisance complaint risk under various weather conditions.',
  category: 'analytics',
  status: 'available',
  icon: 'CloudArrowUpIcon',
  accentColor: 'cyan',
  heroImage: '/pluvius-hero.jpg',
  features: [
    {
      title: 'Gaussian Plume Physics',
      description: 'Industry-standard Gaussian dispersion model for odor transport prediction.',
      icon: 'CloudIcon',
    },
    {
      title: 'Stability Class Analysis',
      description: 'Analyzes Pasquill-Gifford stability classes to determine dispersion coefficients.',
      icon: 'SunIcon',
    },
    {
      title: 'Receptor Concentration Prediction',
      description: 'Predicts odor unit concentrations at residences, roads, and public areas.',
      icon: 'HomeModernIcon',
    },
    {
      title: 'Complaint Risk Assessment',
      description: 'Assesses probability of odor complaints based on concentration vs. annoyance threshold.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Multi-Source Modeling',
      description: 'Models multiple emission sources (pens, lagoons, composting) simultaneously.',
      icon: 'Square3Stack3DIcon',
    },
    {
      title: 'Mitigation Recommendations',
      description: 'Recommends odor control measures based on source contributions.',
      icon: 'WrenchScrewdriverIcon',
    },
  ],
  useCases: [
    'CAFO odor impact assessment',
    'Setback distance verification',
    'Nuisance complaint risk evaluation',
    'Expansion permit support',
    'Odor control planning',
    'Neighbor relations management',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited analyses',
        'Gaussian plume modeling',
        'Multi-source simulation',
        'Complaint risk assessment',
        'Mitigation recommendations',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Dispersion Model', value: 'Gaussian Plume' },
    { label: 'ML Enhancement', value: 'XGBoost emission factors' },
    { label: 'Stability Classes', value: 'Pasquill-Gifford A-F' },
    { label: 'Odor Thresholds', value: '2 (detect), 7 (annoy), 15 (nuisance) OU' },
    { label: 'Source Types', value: 'Area, point, volume' },
    { label: 'Output Format', value: 'Concentration map + risk' },
  ],
  docsUrl: '/docs/pluvius',
  workflowStages: ['site_assessment', 'risk_analysis'],
  domains: ['air_quality', 'feedyard_environmental'],
  industries: ['agriculture', 'cafo_operations'],
};

// PADDOCK product definition
export const PADDOCK_PRODUCT: Product = {
  id: 'paddock',
  slug: 'paddock',
  name: 'PADDOCK',
  tagline: 'Soil Contamination Predictor',
  description: 'Soil contamination assessment for feedyard pen areas and waste handling sites. Predict soil health impacts, identify contamination hotspots, and estimate remediation requirements for TCEQ closure.',
  category: 'analytics',
  status: 'available',
  icon: 'MapIcon',
  accentColor: 'cyan',
  heroImage: '/paddock-hero.jpg',
  features: [
    {
      title: 'Soil Health Scoring',
      description: 'Comprehensive soil health score (0-100) based on nutrient, metal, salinity, and organic factors.',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Contamination Rating',
      description: 'Classifies soil from clean to severe based on TCEQ PCL exceedances.',
      icon: 'TagIcon',
    },
    {
      title: 'Hotspot Identification',
      description: 'Identifies areas requiring remediation based on contamination patterns.',
      icon: 'FireIcon',
    },
    {
      title: 'Remediation Cost Estimation',
      description: 'Estimates soil remediation costs by area and contamination level.',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'PCL Comparison',
      description: 'Compares soil concentrations to TCEQ Protective Concentration Levels.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'Spatial Analysis',
      description: 'Maps contamination distribution across facility for closure planning.',
      icon: 'MapIcon',
    },
  ],
  useCases: [
    'Feedyard soil assessment',
    'Closure liability estimation',
    'Remediation planning',
    'Due diligence investigations',
    'TCEQ compliance demonstration',
    'Land use change evaluation',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'Soil health scoring',
        'Hotspot identification',
        'Cost estimation',
        'PCL comparison',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Health Factors', value: 'Nutrient, metal, salinity, organic' },
    { label: 'ML Model', value: 'XGBoost health predictor' },
    { label: 'Contaminants', value: 'NO3, P, Cl, Zn, Cu, salts' },
    { label: 'Standards', value: 'TCEQ PCLs (Tier 1)' },
    { label: 'Spatial Resolution', value: 'User-defined grid' },
    { label: 'Output Format', value: 'Health map + remediation plan' },
  ],
  docsUrl: '/docs/paddock',
  workflowStages: ['site_assessment', 'decommissioning'],
  domains: ['soil_management', 'feedyard_environmental'],
  industries: ['agriculture', 'cafo_operations', 'environmental_consulting'],
};

// PLAYA product definition
export const PLAYA_PRODUCT: Product = {
  id: 'playa',
  slug: 'playa',
  name: 'PLAYA',
  tagline: 'Surface Water Impact Predictor',
  description: 'Runoff and pollutant loading model for feedyard impacts on surface water. Predict nitrogen, phosphorus, and TSS loads to receiving waters using SCS curve number hydrology.',
  category: 'analytics',
  status: 'available',
  icon: 'ArrowTrendingDownIcon',
  accentColor: 'cyan',
  heroImage: '/playa-hero.jpg',
  features: [
    {
      title: 'SCS Runoff Modeling',
      description: 'Calculates runoff volumes using NRCS curve number method with soil and land cover data.',
      icon: 'CloudArrowDownIcon',
    },
    {
      title: 'Pollutant Loading Calculation',
      description: 'Estimates annual nitrogen, phosphorus, and TSS loads to receiving waters.',
      icon: 'CalculatorIcon',
    },
    {
      title: 'Water Body Impact Rating',
      description: 'Rates impact on playa lakes, streams, and ponds from minimal to severe.',
      icon: 'TagIcon',
    },
    {
      title: 'TCEQ Standard Comparison',
      description: 'Compares predicted concentrations to TCEQ surface water quality standards.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'BMP Effectiveness',
      description: 'Evaluates best management practice effectiveness for load reduction.',
      icon: 'ShieldCheckIcon',
    },
    {
      title: 'Setback Analysis',
      description: 'Assesses compliance with required setbacks from water bodies.',
      icon: 'MapPinIcon',
    },
  ],
  useCases: [
    'CAFO surface water protection',
    'Runoff control design',
    'BMP selection and sizing',
    'Water quality compliance',
    'Playa lake protection',
    'TMDL source assessment',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited analyses',
        'Runoff modeling',
        'Loading calculations',
        'Impact ratings',
        'BMP evaluation',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Hydrology Model', value: 'SCS Curve Number (TR-55)' },
    { label: 'ML Enhancement', value: 'XGBoost loading calibration' },
    { label: 'Pollutants', value: 'TN, TP, TSS, E. coli' },
    { label: 'Water Bodies', value: 'Playa, stream, pond, drainage' },
    { label: 'Standards', value: 'TCEQ Surface Water Quality' },
    { label: 'Output Format', value: 'Loading report + impact rating' },
  ],
  docsUrl: '/docs/playa',
  workflowStages: ['site_assessment', 'risk_analysis'],
  domains: ['water_quality', 'feedyard_environmental'],
  industries: ['agriculture', 'cafo_operations', 'environmental_consulting'],
};

// PARTICULATE product definition
export const PARTICULATE_PRODUCT: Product = {
  id: 'particulate',
  slug: 'particulate',
  name: 'PARTICULATE',
  tagline: 'Dust/PM Emission Predictor',
  description: 'Particulate matter emission modeling for feedyard operations. Predict PM2.5, PM10, and TSP concentrations at receptors using EPA AP-42 emission factors and Gaussian dispersion.',
  category: 'analytics',
  status: 'available',
  icon: 'CloudArrowUpIcon',
  accentColor: 'cyan',
  heroImage: '/particulate-hero.jpg',
  features: [
    {
      title: 'AP-42 Emission Factors',
      description: 'Calculates emissions using EPA AP-42 factors for feedlot pen surfaces and unpaved roads.',
      icon: 'CalculatorIcon',
    },
    {
      title: 'PM Size Distribution',
      description: 'Models PM2.5, PM10, and TSP fractions from each emission source.',
      icon: 'Square3Stack3DIcon',
    },
    {
      title: 'Receptor Concentration Prediction',
      description: 'Predicts 24-hour and annual PM concentrations at nearby receptors.',
      icon: 'HomeModernIcon',
    },
    {
      title: 'NAAQS Comparison',
      description: 'Compares predictions to EPA National Ambient Air Quality Standards.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'Health Risk Assessment',
      description: 'Classifies health risk from low to very unhealthy based on AQI categories.',
      icon: 'HeartIcon',
    },
    {
      title: 'Control Measure Evaluation',
      description: 'Evaluates dust control effectiveness (watering, windbreaks, surface treatment).',
      icon: 'WrenchScrewdriverIcon',
    },
  ],
  useCases: [
    'Feedyard dust impact assessment',
    'Air quality permit support',
    'Dust control planning',
    'Neighbor complaint response',
    'Health risk evaluation',
    'Expansion impact analysis',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited analyses',
        'Emission calculations',
        'Dispersion modeling',
        'NAAQS comparison',
        'Health risk assessment',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Emission Model', value: 'EPA AP-42 Factors' },
    { label: 'Dispersion Model', value: 'Gaussian Plume' },
    { label: 'PM Fractions', value: 'PM2.5, PM10, TSP' },
    { label: 'NAAQS Standards', value: 'PM2.5: 35/12, PM10: 150 µg/m³' },
    { label: 'Source Types', value: 'Pen, road, windblown, handling' },
    { label: 'Output Format', value: 'Concentration + health risk' },
  ],
  docsUrl: '/docs/particulate',
  workflowStages: ['site_assessment', 'risk_analysis'],
  domains: ['air_quality', 'feedyard_environmental'],
  industries: ['agriculture', 'cafo_operations'],
};

// Oilfield Remediation Products
export const PURGE_PRODUCT: Product = {
  id: 'purge',
  slug: 'purge',
  name: 'PURGE',
  tagline: 'Brine Soil Remediation Optimizer',
  description: 'Science-based treatment recommendations for salt-contaminated soils from oilfield operations. Uses Sanchez et al. (2019) research to optimize remediation strategies based on EC levels, soil texture, and site conditions.',
  category: 'remediation',
  status: 'available',
  icon: 'BeakerIcon',
  accentColor: 'cyan',
  heroImage: '/purge-hero.jpg',
  features: [
    {
      title: 'EC-Based Severity Classification',
      description: 'Classifies contamination severity using research-validated EC thresholds (<4, 4-8, 8-25, >25 dS/m).',
      icon: 'ChartBarIcon',
    },
    {
      title: 'Treatment Effectiveness Matrix',
      description: 'Evaluates treatment options based on soil texture (sandy/loamy/clayey) and contamination severity.',
      icon: 'TableCellsIcon',
    },
    {
      title: 'Phased Approach Design',
      description: 'Generates multi-phase treatment plans for high/severe contamination requiring sequential treatments.',
      icon: 'QueueListIcon',
    },
    {
      title: 'Cost-Benefit Analysis',
      description: 'Compares treatment costs from $500/acre (natural recovery) to $25,000/acre (excavation).',
      icon: 'CurrencyDollarIcon',
    },
    {
      title: 'Scientific Rationales',
      description: 'Provides peer-reviewed scientific basis for each recommendation (Sanchez 2019).',
      icon: 'AcademicCapIcon',
    },
    {
      title: 'Monitoring Plan Generation',
      description: 'Creates customized monitoring protocols with field and lab parameters, success criteria.',
      icon: 'ClipboardDocumentCheckIcon',
    },
  ],
  useCases: [
    'Produced water spill remediation',
    'Legacy pit closure planning',
    'Tank battery leak cleanup',
    'Pipeline release response',
    'Drilling fluid pit remediation',
    'Oilfield site decommissioning',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited site analyses',
        'Treatment recommendations',
        'Phased approach design',
        'Monitoring plan generation',
        'Scientific rationales',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Scientific Basis', value: 'Sanchez et al. (2019)' },
    { label: 'EC Thresholds', value: '4, 8, 25 dS/m' },
    { label: 'SAR Threshold', value: '13 (sodicity)' },
    { label: 'Soil Categories', value: 'Sandy, Loamy, Clayey' },
    { label: 'Treatment Options', value: '8 methods evaluated' },
    { label: 'Output Format', value: 'Recommendations + monitoring plan' },
  ],
  docsUrl: '/docs/purge',
  workflowStages: ['remediation_planning', 'site_assessment', 'decommissioning'],
  domains: ['remediation_optimization', 'oil_gas_operations', 'soil_management'],
  industries: ['environmental_consulting', 'oil_gas'],
};

// PRECEPT product definition
export const PRECEPT_PRODUCT: Product = {
  id: 'precept',
  slug: 'precept',
  name: 'PRECEPT',
  tagline: 'Multi-State Waste Characterization Tool',
  description: 'Decision tree-based waste classification for E&P and industrial wastes. Supports RCRA hazardous determination, NORM assessment, E&P exemption analysis, and state-specific land application eligibility across 8 states.',
  category: 'compliance',
  status: 'available',
  icon: 'ExclamationTriangleIcon',
  accentColor: 'cyan',
  heroImage: '/precept-hero.jpg',
  features: [
    {
      title: 'RCRA Hazardous Determination',
      description: 'Evaluates waste against all four RCRA characteristics: ignitability, corrosivity, reactivity, and toxicity (TCLP).',
      icon: 'ShieldExclamationIcon',
    },
    {
      title: 'E&P Exemption Analysis',
      description: 'Determines eligibility for RCRA Subtitle C exemption based on waste source, type, and handling history.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'NORM/TENORM Assessment',
      description: 'Compares radium-226 and radium-228 levels against state-specific thresholds (ND most restrictive at 5 pCi/g).',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Land Application Eligibility',
      description: 'Evaluates chloride, TPH, BTEX, EC, and SAR against state thresholds for landfarming/land application.',
      icon: 'GlobeAmericasIcon',
    },
    {
      title: 'Multi-State Support',
      description: 'Complete regulatory frameworks for TX, NM, OK, CO, LA, ND, WY, and PA with state-specific thresholds.',
      icon: 'MapIcon',
    },
    {
      title: 'Disposal Recommendations',
      description: 'Provides disposal options with cost ranges, permit requirements, and restrictions based on classification.',
      icon: 'TruckIcon',
    },
  ],
  useCases: [
    'E&P waste characterization for drilling and production wastes',
    'NORM waste management decision support',
    'Land application eligibility screening',
    'Disposal facility selection',
    'Multi-state regulatory compliance',
    'Waste tracking and documentation',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited classifications',
        'All 8 supported states',
        'RCRA hazardous determination',
        'NORM assessment',
        'Land application screening',
        'Disposal recommendations',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Classification Engine', value: 'Decision Tree (Rule-Based)' },
    { label: 'RCRA Characteristics', value: 'D001-D011, D018, D039, D040' },
    { label: 'NORM Standards', value: '8 state-specific thresholds' },
    { label: 'Land App Parameters', value: 'Cl, TPH, BTEX, EC, SAR' },
    { label: 'States Supported', value: 'TX, NM, OK, CO, LA, ND, WY, PA' },
    { label: 'Output Format', value: 'Classification + recommendations' },
  ],
  docsUrl: '/docs/precept',
  workflowStages: ['site_assessment', 'risk_analysis'],
  domains: ['regulatory_compliance', 'oil_gas_operations'],
  industries: ['environmental_consulting', 'oil_gas'],
};

// PREMISE product definition
export const PREMISE_PRODUCT: Product = {
  id: 'premise',
  slug: 'premise',
  name: 'PREMISE',
  tagline: 'Automated Phase I/II/III Environmental Site Assessment Engine',
  description: 'ML-powered environmental due diligence for property transactions. Predicts REC likelihood, contaminants of concern, Phase II needs, and provides deal risk assessment with transaction recommendations for real estate lending and M&A.',
  category: 'analysis',
  status: 'available',
  icon: 'BuildingOffice2Icon',
  accentColor: 'cyan',
  heroImage: '/premise-hero.jpg',
  features: [
    {
      title: 'Phase I REC Prediction',
      description: 'Predicts likelihood of Recognized Environmental Conditions (RECs), Historical RECs, and Controlled RECs based on site history and NAICS codes.',
      icon: 'DocumentMagnifyingGlassIcon',
    },
    {
      title: 'Contaminant of Concern Identification',
      description: 'Identifies likely COCs including BTEX, Chlorinated Solvents, Metals, PAHs, PCBs, and more based on property use history.',
      icon: 'BeakerIcon',
    },
    {
      title: 'Phase II Recommendation',
      description: 'Determines if Phase II ESA is warranted, recommends sampling approach (soil, groundwater, vapor), and estimates investigation scope.',
      icon: 'ClipboardDocumentCheckIcon',
    },
    {
      title: 'Phase III Remediation Estimates',
      description: 'When contamination is identified, predicts remediation costs, timeline, and regulatory pathway requirements.',
      icon: 'WrenchScrewdriverIcon',
    },
    {
      title: 'Deal Risk Assessment',
      description: 'Classifies transactions into risk tiers (Minimal, Moderate, Elevated, High, Critical) with quantified financial exposure.',
      icon: 'ShieldExclamationIcon',
    },
    {
      title: 'Transaction Recommendations',
      description: 'Provides clear guidance: Proceed, Proceed with Conditions, Negotiate, Negotiate with Escrow, or Walk Away.',
      icon: 'CheckBadgeIcon',
    },
  ],
  useCases: [
    'Real estate M&A environmental due diligence',
    'Bank lending environmental risk screening',
    'Property acquisition Phase I scoping',
    'Portfolio environmental liability assessment',
    'Brownfield redevelopment feasibility',
    'Insurance underwriting risk evaluation',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited property assessments',
        'Full Phase I/II/III prediction',
        'Deal risk scoring',
        'Transaction recommendations',
        'Remediation cost estimates',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'ML Framework', value: 'XGBoost Ensemble' },
    { label: 'Input Features', value: '38 site characteristics' },
    { label: 'Target Models', value: '10 prediction outputs' },
    { label: 'REC Categories', value: 'De Minimis to Significant' },
    { label: 'Risk Tiers', value: 'Minimal to Critical (5 levels)' },
    { label: 'Output Format', value: 'Assessment + recommendations' },
  ],
  docsUrl: '/docs/premise',
  workflowStages: ['site_assessment', 'risk_analysis', 'financial_planning'],
  domains: ['environmental_due_diligence', 'real_estate'],
  industries: ['environmental_consulting', 'banking_finance', 'real_estate'],
};

// PHREATIC product definition
export const PHREATIC_PRODUCT: Product = {
  id: 'phreatic',
  slug: 'phreatic',
  name: 'PHREATIC',
  tagline: 'Groundwater Availability Modeling and Prediction Engine',
  description: 'Per-aquifer prediction platform for groundwater availability and water-table behavior. Distinct models for each major Texas aquifer — Edwards, Ogallala, Gulf Coast, Dockum, and a general fallback — with a spatial classifier routing every well to the right one. Aquifer-aware well placement intelligence, cone-of-depression modeling, and the proprietary Floyd Factor aquifer-health metric.',
  category: 'analysis',
  status: 'available',
  icon: 'PhreaticIcon',
  accentColor: 'cyan',
  heroImage: '/phreatic-hero.jpg',
  features: [
    {
      title: 'Hydrograph',
      description: 'Professional-grade multi-well water-level chart. Model prediction overlay with confidence intervals, per-well visibility toggles, aquifer filtering, crosshair tooltip, scroll-zoom on either axis.',
      icon: 'ChartBarIcon',
    },
    {
      title: '3D Water Table',
      description: 'Interactive 3D surface of the water table with well markers placed on the surface. Vertical exaggeration, hover labels, and contour overlays — animated forward through every prediction horizon.',
      icon: 'CubeTransparentIcon',
    },
    {
      title: 'Well Map & Cross-Section',
      description: '2D satellite map with well markers and smooth water-table contours laid over high-resolution imagery, plus a vertical geological profile showing ground surface, water table, and aquifer base along any user-drawn line.',
      icon: 'MapIcon',
    },
    {
      title: 'Well Intelligence',
      description: 'Placement engine — recommends WHERE to drill and HOW DEEP. Aquifer-strict (every candidate inside the right hydrologic unit) and parcel-bounded (inside your property boundary) with physics-based interference scoring.',
      icon: 'MapPinIcon',
    },
    {
      title: 'Well Inventory',
      description: 'All site well assets in one table — imported wells + model-recommended placements. CSV import, manual gauging entry, and continuous re-prediction on every new reading.',
      icon: 'TableCellsIcon',
    },
    {
      title: 'Cone of Depression',
      description: 'Physics-based drawdown cross-sections for single wells and full wellfields. ROI map shows interference radius around each pumping well — driven by use-case pump rate.',
      icon: 'ArrowTrendingDownIcon',
    },
    {
      title: 'Sustainable Yield & Pump Control',
      description: 'Maximum sustainable pump rate that holds saturated thickness above threshold for a target lifespan. Interference-aware pump scheduling generates optimal on/off cycles across all wells and tracks conflicts.',
      icon: 'BoltIcon',
    },
    {
      title: 'Cavitation Risk Timeline',
      description: 'Per-well cavitation risk from 3 months to 20 years out — early warning system flagging which wells will lose pump submergence first under the current drawdown trajectory.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Floyd Factor',
      description: 'Proprietary aquifer-health composite that ties drawdown history, recharge behavior, and aquifer-specific properties into a single tier-crossing signal that drives alerts.',
      icon: 'SparklesIcon',
    },
    {
      title: 'Volume, Economics & Water Quality',
      description: 'Available groundwater volume using industry-standard hydrogeology (Fetter). Demand modeling, water-rights valuation, cost analysis, and crop trade-offs. TDS, nitrate, arsenic, fluoride checked against MCLs for livestock, irrigation, and drinking suitability.',
      icon: 'CalculatorIcon',
    },
    {
      title: 'Lending Risk & TCEQ 230 Report',
      description: 'Structured groundwater availability report — Approve / Approve with Conditions / Additional Review / Decline. TCEQ Chapter 230 categorization, GCD jurisdiction, drought resilience, print-ready PDF.',
      icon: 'DocumentCheckIcon',
    },
    {
      title: 'Per-Aquifer Engine',
      description: 'Distinct prediction models for each major Texas aquifer (Edwards, Ogallala, Gulf Coast, Dockum, plus a general fallback). A spatial classifier routes each well to the right model — no cross-aquifer averaging, no one-size-fits-all forecasting.',
      icon: 'CpuChipIcon',
    },
  ],
  useCases: [
    'Bank lending decisions for agricultural and rural property transactions',
    'Subdivision plat certification (TCEQ Chapter 230) for real estate developers',
    'Data center site selection — verify long-term cooling water availability before breaking ground',
    'Ranch and farmland acquisition due diligence with aquifer-specific drawdown projections',
    'Municipal and industrial water supply planning with drought resilience and Floyd Factor alerts',
    'Agriculture operations — irrigation sustainability, aquifer drawdown, water rights valuation',
    'Drought risk assessment for property and infrastructure portfolios',
    'GCD permit application support with parcel-bounded well placement intelligence',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited property certifications',
        'Per-aquifer prediction models (Edwards, Ogallala, Gulf Coast, Dockum, General fallback) across multiple horizons through 20 years',
        'Spatial aquifer classifier that routes each well to the right model',
        'Aquifer-strict, parcel-bounded well placement intelligence',
        'Floyd Factor aquifer-health metric',
        'Professional-grade hydrograph with model prediction overlays',
        'Cone of depression and pump-schedule optimizer',
        'Lending risk + TCEQ 230 + GCD compliance',
        'Full API access',
        'Custom integrations',
        'Dedicated success manager',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'ML Framework', value: 'Per-aquifer prediction models with a stacked ML ensemble per bundle' },
    { label: 'Aquifer Coverage', value: 'Edwards / Ogallala / Gulf Coast / Dockum / General fallback' },
    { label: 'Prediction Horizons', value: 'Near-term through 20 years, with multiple intermediate marks' },
    { label: 'Classifier', value: 'Spatial routing to the right per-aquifer model' },
    { label: 'Placement Intelligence', value: 'Physics-based scoring on a parcel-gated candidate grid' },
    { label: 'Health Metric', value: 'Floyd Factor — proprietary composite of drawdown, recharge, and aquifer behavior' },
    { label: 'Chart Engine', value: 'Professional-grade time-series charting' },
    { label: 'Certification Status', value: '5 categories per TCEQ Chapter 230' },
  ],
  docsUrl: '/docs/phreatic',
  workflowStages: ['site_assessment', 'risk_analysis', 'financial_planning'],
  domains: ['water_resources', 'real_estate'],
  industries: ['environmental_consulting', 'banking_finance', 'agriculture'],
};

// DATA VAULT product definition
export const DATAVAULT_PRODUCT: Product = {
  id: 'datavault',
  slug: 'datavault',
  name: 'DATA VAULT',
  tagline: 'Environmental Data Management Platform',
  description: 'Centralized environmental data management and analytics platform for contaminated groundwater sites. Multi-dimensional data exploration across analytical chemistry, water levels, risk predictions, cost projections, plume spatial data, and compliance tracking — with advanced anomaly detection, site comparison, and export capabilities.',
  category: 'document_management',
  status: 'available',
  icon: 'TableCellsIcon',
  accentColor: 'cyan',
  features: [
    {
      title: 'Analytical Chemistry Explorer',
      description: 'Browse and filter contaminant sampling data across sites, wells, and analytes. Non-detect handling strategies, MCL exceedance flagging, and unit conversion between mg/L and µg/L.',
      icon: 'BeakerIcon',
    },
    {
      title: 'Risk Predictions & Scoring',
      description: 'Model-driven site risk scoring on a 0-100 scale with threat tier classification. Closure timeline projections, enforcement probability, bottleneck analyte identification, and cost-to-closure estimates.',
      icon: 'ExclamationTriangleIcon',
    },
    {
      title: 'Anomaly Detection',
      description: 'Z-score based outlier flagging across analytical results. Automatically identifies data errors, unexpected concentration spikes, and environmental changes with configurable sensitivity thresholds.',
      icon: 'BellAlertIcon',
    },
    {
      title: 'Site Comparison',
      description: 'Side-by-side comparison of two sites or two time periods. Analyte statistics, percent change calculations, and multi-analyte comparison matrices for portfolio-level analysis.',
      icon: 'MagnifyingGlassIcon',
    },
    {
      title: 'Compliance Tracking',
      description: 'Track MCL exceedances, sampling completeness, and regulatory compliance across your portfolio. Days since last sample, wells sampled, and compliance percentage at a glance.',
      icon: 'ClipboardDocumentCheckIcon',
    },
    {
      title: 'Multi-Format Export',
      description: 'Export data as CSV, multi-sheet XLSX, GeoJSON for GIS integration, bulk ZIP archives, and formatted PDF reports. Full audit logging of all exports for data governance.',
      icon: 'ArrowDownTrayIcon',
    },
    {
      title: 'Sampling Plan',
      description: 'Build per-site sampling plans — assign wells, analyte groups, and methods, set recurrence from monthly through annual, and move plans through draft, active, and archived states. Container-cart bottle counts, sampling-team assignment, downloadable field checklists, and a calendar that schedules every event.',
      icon: 'CalendarDaysIcon',
    },
    {
      title: 'Field Intake App',
      description: 'Submit structured field data straight from the site — gauging, field parameters, and sample collection — with GPS capture and multi-entry batches. Electronic data deliverable (EDD) import with batch QA/QC review feeds lab results into the same pipeline.',
      icon: 'InboxIcon',
    },
    {
      title: 'Custom Charts & Grid',
      description: 'Advanced visualizations beyond the standard line chart — Sankey contamination-pathway flows, streamgraphs, per-well small multiples, and groundwater flow-direction maps. A sortable, filterable analytical grid with inline trend sparklines and a status badge on every row.',
      icon: 'ChartBarSquareIcon',
    },
    {
      title: 'Regulatory Library',
      description: 'Federal and state regulatory standards libraries with a per-site cascade. Site-specific limits — background, cleanup goal, consent order, permit, action level, MNA goal — plus configurable exceedance rules and styled reference lines drawn on every chart.',
      icon: 'ScaleIcon',
    },
    {
      title: 'Flag Codes',
      description: 'Tenant-managed data-qualifier flag codes for gauging and analytical results. Define codes and descriptions — non-detect, below-MCL, exceedance, and custom QA flags — applied across the grid so every value carries its data-quality context.',
      icon: 'DocumentCheckIcon',
    },
  ],
  useCases: [
    'Portfolio-wide environmental data analysis across hundreds of contaminated sites',
    'Regulatory compliance reporting and MCL exceedance tracking for state agencies',
    'Concentration trend analysis and anomaly detection for early warning systems',
    'Site comparison for remediation prioritization and capital allocation',
    'Water level monitoring and gauging data management with product thickness tracking',
    'Plume spatial analysis with migration rate tracking and well-at-risk predictions',
  ],
  pricing: [
    {
      tier: 'enterprise',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited sites and wells',
        'All 6 data tabs (Analytical, Gauging, Risk, Cost, Plume, Compliance)',
        'Anomaly detection and comparison tools',
        'Multi-format export (CSV, XLSX, GeoJSON, PDF)',
        'Saved filter presets',
        'Full audit logging',
        'Dedicated support',
      ],
      siteLimit: null,
      userLimit: null,
    },
  ],
  technicalSpecs: [
    { label: 'Storage', value: 'Unlimited cloud storage (GCS)' },
    { label: 'Data Formats', value: 'PDF, XLSX, CSV, GeoJSON, ZIP' },
    { label: 'Data Tabs', value: '6 (Analytical, Gauging, Risk, Cost, Plume, Compliance)' },
    { label: 'Anomaly Engine', value: 'Z-score based, configurable threshold (default 2.0σ)' },
    { label: 'Unit Conversion', value: 'Server-side mg/L ↔ µg/L' },
    { label: 'Pagination', value: '50 items/page with lazy tab loading' },
  ],
  workflowStages: ['site_assessment', 'performance_monitoring'],
  domains: ['regulatory_compliance', 'groundwater_transport'],
  industries: ['environmental_consulting', 'oil_gas', 'regulatory_affairs'],
};

// All products catalog (add more as they're developed)
export const PRODUCTS_CATALOG: Product[] = [
  // Featured Products (displayed on Landing page)
  POLYGON_PRODUCT,
  PRECEPT_PRODUCT,
  PATHFINDER_PRODUCT,
  // Core Products
  PULSE_PRODUCT,
  PERMEATE_PRODUCT,
  PLUNGE_PRODUCT,
  PEAK_PRODUCT,
  PHASE_PRODUCT,
  PROXY_PRODUCT,
  PERSIST_PRODUCT,
  PIPELINE_PRODUCT,
  PROTOCOL_PRODUCT,
  PINNACLE_PRODUCT,
  PLUG_PRODUCT,
  POUNCE_PRODUCT,
  PARALLAX_PRODUCT,
  PRECIPITATE_PRODUCT,
  PERPETUAL_PRODUCT,
  PROTON_PRODUCT,
  PYRITIC_PRODUCT,
  // Feedyard Environmental Products
  PASTURE_PRODUCT,
  POND_PRODUCT,
  PERMIT_PRODUCT,
  PERCOLATE_PRODUCT,
  PEN_PRODUCT,
  PATHOGEN_FEEDYARD_PRODUCT,
  PLUVIUS_PRODUCT,
  PADDOCK_PRODUCT,
  PLAYA_PRODUCT,
  PARTICULATE_PRODUCT,
  // Oilfield Remediation Products
  PURGE_PRODUCT,
  // Environmental Due Diligence Products
  PREMISE_PRODUCT,
  PHREATIC_PRODUCT,
  // Data & Utilities
  DATAVAULT_PRODUCT,
];

// Helper functions
export function getProductBySlug(slug: string): Product | undefined {
  return PRODUCTS_CATALOG.find(p => p.slug === slug);
}

export function getProductsByCategory(category: ProductCategory): Product[] {
  return PRODUCTS_CATALOG.filter(p => p.category === category);
}

export function getAvailableProducts(): Product[] {
  return PRODUCTS_CATALOG.filter(p => p.status === 'available' || p.status === 'beta');
}

// Filter dimension helpers
export function getProductsByWorkflowStage(stage: WorkflowStage): Product[] {
  return PRODUCTS_CATALOG.filter(p => p.workflowStages.includes(stage));
}

export function getProductsByDomain(domain: ProductDomain): Product[] {
  return PRODUCTS_CATALOG.filter(p => p.domains.includes(domain));
}

export function getProductsByIndustry(industry: ProductIndustry): Product[] {
  return PRODUCTS_CATALOG.filter(p => p.industries.includes(industry));
}

// Marketplace filter interface
export interface MarketplaceFilters {
  workflowStages: WorkflowStage[];
  domains: ProductDomain[];
  industries: ProductIndustry[];
}

// Filter products using OR logic across all dimensions
export function filterProducts(filters: MarketplaceFilters): Product[] {
  const hasFilters =
    filters.workflowStages.length > 0 ||
    filters.domains.length > 0 ||
    filters.industries.length > 0;

  if (!hasFilters) {
    return PRODUCTS_CATALOG;
  }

  return PRODUCTS_CATALOG.filter(product => {
    // OR logic: product matches if it belongs to ANY selected filter
    const matchesStage = filters.workflowStages.length === 0 ||
      filters.workflowStages.some(stage => product.workflowStages.includes(stage));

    const matchesDomain = filters.domains.length === 0 ||
      filters.domains.some(domain => product.domains.includes(domain));

    const matchesIndustry = filters.industries.length === 0 ||
      filters.industries.some(industry => product.industries.includes(industry));

    // With OR logic, return true if ANY dimension matches (when that dimension has filters)
    if (filters.workflowStages.length > 0 && matchesStage) return true;
    if (filters.domains.length > 0 && matchesDomain) return true;
    if (filters.industries.length > 0 && matchesIndustry) return true;

    return false;
  });
}

// Get ordered workflow stages for display
export function getOrderedWorkflowStages(): WorkflowStage[] {
  return (Object.keys(WORKFLOW_STAGES) as WorkflowStage[])
    .sort((a, b) => WORKFLOW_STAGES[a].order - WORKFLOW_STAGES[b].order);
}

// Group products by workflow stage (for section display)
// Each product only appears once, in its first (primary) workflow stage
export function getProductsGroupedByWorkflowStage(products: Product[]): Record<WorkflowStage, Product[]> {
  const grouped: Record<WorkflowStage, Product[]> = {
    site_assessment: [],
    risk_analysis: [],
    remediation_planning: [],
    performance_monitoring: [],
    emergency_response: [],
    decommissioning: [],
  };

  // Track which products have already been assigned to avoid duplicates
  const assignedProducts = new Set<string>();

  // Order stages by their defined order so products go in their earliest stage
  const orderedStages: WorkflowStage[] = [
    'site_assessment',
    'risk_analysis',
    'remediation_planning',
    'performance_monitoring',
    'emergency_response',
    'decommissioning',
  ];

  orderedStages.forEach(stage => {
    products.forEach(product => {
      if (product.workflowStages.includes(stage) && !assignedProducts.has(product.id)) {
        grouped[stage].push(product);
        assignedProducts.add(product.id);
      }
    });
  });

  return grouped;
}
